/**
 * Shared constants + helpers for the Einstufung-Tool steps.
 */
export const CONFIDENCE_HIGH = 0.9;

export const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png", ".docx"];
export const ALLOWED_MIME = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
export const MAX_FILE_BYTES = 15 * 1024 * 1024;
export const MAX_FILES = 10;
export const MAX_TEXT = 8000;

/**
 * Iter 20.36 — KV-spezifische Terminologie für die Übereinstimmungs-Prüfung.
 *
 * Der KV „Angestellte bei Steuerberatern und Wirtschaftsprüfern" spricht
 * statt „Gleichwertigkeit" von „Facheinschlägigkeit". Die Semantik ist
 * identisch, aber das Wording zieht sich durch UI-Badges, Zeilen-Hinweise,
 * Filter-Chips im Admin und die LLM-Prompts (letztere via Backend).
 *
 * Vier Wort-Formen:
 *   - noun:     Substantiv Nom. („Gleichwertigkeit" / „Facheinschlägigkeit")
 *   - adj:      Adjektiv     („gleichwertig"     / „facheinschlägig")
 *   - pruefung: Compound     („Gleichwertigkeitsprüfung" / „Facheinschlägigkeitsprüfung")
 *   - kind:     Konstante Kennung des KV-Feld-Werts
 */
export const EQUIVALENCE_TERM_MAP = Object.freeze({
  gleichwertigkeit: Object.freeze({
    noun: "Gleichwertigkeit",
    adj: "gleichwertig",
    pruefung: "Gleichwertigkeitsprüfung",
    // Iter 20.36.f1 — Qualifizierte Varianten, um Tautologie „fachliche
    // Facheinschlägigkeit" zu vermeiden. Bei „gleichwertigkeit" bleibt
    // das explizite „fachlich"-Adjektiv erhalten.
    qualified_noun: "fachliche Gleichwertigkeit",
    qualified_adj: "fachlich gleichwertig",
    qualified_pruefung: "fachliche Gleichwertigkeitsprüfung",
    kind: "gleichwertigkeit",
  }),
  facheinschlaegigkeit: Object.freeze({
    noun: "Facheinschlägigkeit",
    adj: "facheinschlägig",
    pruefung: "Facheinschlägigkeitsprüfung",
    // „Facheinschlägigkeit" enthält „fachlich" bereits im Wort selbst →
    // qualified_* verzichten auf das zusätzliche „fachlich".
    qualified_noun: "Facheinschlägigkeit",
    qualified_adj: "facheinschlägig",
    qualified_pruefung: "Facheinschlägigkeitsprüfung",
    kind: "facheinschlaegigkeit",
  }),
});

export const DEFAULT_EQUIVALENCE_TERMINOLOGY = "gleichwertigkeit";

/**
 * Liefert die 4-Feld-Terminologie-Struktur passend zum KV.
 * Akzeptiert entweder das KV-Objekt (mit `.equivalence_terminology`) oder
 * einen rohen String. Unbekannte Werte fallen auf den Default zurück.
 */
export function getEquivalenceTerms(kvOrTerminology) {
  const raw =
    kvOrTerminology && typeof kvOrTerminology === "object"
      ? kvOrTerminology.equivalence_terminology
      : kvOrTerminology;
  const key = String(raw || DEFAULT_EQUIVALENCE_TERMINOLOGY).trim().toLowerCase();
  return (
    EQUIVALENCE_TERM_MAP[key] || EQUIVALENCE_TERM_MAP[DEFAULT_EQUIVALENCE_TERMINOLOGY]
  );
}

export function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function fileKind(f) {
  const n = (f.name || "").toLowerCase();
  if (n.endsWith(".pdf")) return "pdf";
  if (n.endsWith(".png")) return "png";
  if (n.endsWith(".docx")) return "docx";
  return "jpg";
}

/**
 * Iter 19.63.63 — Detection: is this an "Angestellten"-Position?
 *
 * Used for the KV-Gleichwertigkeits-Hinweis in Schritt 3. KVs mit dem
 * Flag `equivalence_check_required=true` (typisch: KV Handel Angestellte,
 * Metallgewerbe Angestellte, …) verlangen, dass jede anrechenbare
 * Angestelltenposition manuell auf fachliche Gleichwertigkeit zur neuen
 * Rolle geprüft wird.
 *
 * Quelle: das `taetigkeit`-Feld stammt aus der SV-Auszug-Extraktion und
 * enthält die original-SV-Klassifizierung (Angestellte, Arbeiter,
 * Lehrling, Notstandshilfe, Wochengeld, Selbständig, …). Substring-
 * Match auf "angestellt" deckt:
 *   • "Angestellter" / "Angestellte" / "Angestellten"
 *   • "Geringfügig beschäftigte Angestellte"
 * Nicht-Matches: Arbeiter, Lehrling, Notstandshilfe, Krankengeld,
 *   Wochengeld, Selbständig, Präsenzdienst, Studierende usw.
 */
export function isAngestelltPosition(taetigkeit) {
  if (!taetigkeit || typeof taetigkeit !== "string") return false;
  return taetigkeit.toLowerCase().includes("angestellt");
}

// ── Iter 19.75 — Deterministische SV-Statuswort-Whitelist (Spiegel zum
//    Backend `_DETERMINISTIC_SV_STATUS_RE`). ────────────────────────────────
// SV-rechtliche Statusbegriffe, die auch aus einem Lebenslauf DETERMINISTISCH
// erkennbar sind — keine LLM-Interpretation nötig. Wenn die Tätigkeit eines
// dieser Wörter enthält, überspringt `classifyFactorProvenance` die
// CV-ANNAHME-Downgrade-Regel und lässt die Zeile als KV-REGEL laufen.
const _DETERMINISTIC_SV_STATUS_RE = new RegExp(
  "\\b(?:" +
    [
      // Deutsch
      "zivildienst",
      "pr(ä|ae)senzdienst",
      "pr(ä|ae)sentdienst",
      "milit(ä|ae)rdienst",
      "bundesheer",
      "karenz(?:zeit)?",
      "mutterschaftskarenz",
      "elternkarenz",
      "v(ä|ae)terkarenz",
      "bildungskarenz",
      "familienhospizkarenz",
      "pflegekarenz",
      "bildungsteilzeit",
      "wochengeld(?:bezug)?",
      "wochenschutz",
      "mutterschutz",
      "krankengeld",
      "ams(?:-bezug|-arbeitslos)?",
      "arbeitslos(?:engeld|igkeit|enhilfe)?",
      "notstandshilfe",
      "(ü|ue)berbr(ü|ue)ckungshilfe",
      "studium",
      "h(ö|oe)here\\s+schule",
      "schulausbildung",
      "lehre",
      "lehrling",
      "haft(?:strafe|zeit)?",
      "gef(ä|ae)ngnis",
      "selbstversicherung",
      "kinderbetreuungsgeld",
      "kbg(?:-bezug|bezug)?",
      "freier\\s+dienstnehmer",
      // Iter 19.76 (Bug 1) — Englische Äquivalente
      "civil\\s+service",
      "community\\s+service",
      "military\\s+service",
      "army\\s+service",
      "national\\s+service",
      "conscription",
      "maternity\\s+leave",
      "parental\\s+leave",
      "paternity\\s+leave",
      "educational\\s+leave",
      "study\\s+leave",
      "care\\s+leave",
      "unemployment\\s+(?:benefit|allowance|insurance)",
      "sick\\s+leave",
      "sickness\\s+benefit",
      "disability\\s+(?:benefit|allowance)",
      "prison(?:\\s+sentence)?",
      "incarceration",
      "self.?insurance",
      "self.?employed",
      "apprentice(?:ship)?",
      "secondary\\s+school",
      "high\\s+school",
      "university\\s+studies?",
      "doctoral\\s+stud(?:y|ies|ent)",
      "bachelor\\s+stud(?:y|ies)",
      "master\\s+stud(?:y|ies)",
      "phd\\s+stud(?:y|ies)",
    ].join("|") +
    ")\\b",
  "i",
);

/**
 * Iter 19.76 (Bug 1) — Prüft Tätigkeit UND Begründung.
 * LLM extrahiert die Tätigkeit oft englisch (aus englischen CVs),
 * formuliert die Begründung aber deutsch mit dem eigentlichen SV-Begriff.
 *
 * Iter 20.36.f8.2 — Nur der ERSTE Chunk der Begründung (vor dem ersten
 * „|"-Trennzeichen) wird für die Row-Level-Klassifikation herangezogen.
 * Alles nach dem Pipe sind Backend-Anhänge (WT-Kanzlei-Regel-Note,
 * Altersfaktor-Note, Overlap-Hinweise auf ANDERE anrechenbare Positionen
 * wie „…überlappen mit anderer anrechenbarer Position (Bezug/Anspruch
 * von/auf pauschalem(s) KBG)…"). Diese Anhänge dürfen die Klassifikation
 * der aktuellen Zeile nicht kippen — sonst würde eine echte Angestellte-
 * Position mit KBG-Overlap-Hinweis fälschlich als SV-Status gewertet und
 * die WT-Kanzlei-Checkbox / Gleichwertigkeitsprüfung-Warndreieck
 * verschwinden.
 */
export function isDeterministicSvStatus(taetigkeit, begruendung) {
  if (taetigkeit && typeof taetigkeit === "string"
      && _DETERMINISTIC_SV_STATUS_RE.test(taetigkeit)) {
    return true;
  }
  if (begruendung && typeof begruendung === "string") {
    const firstChunk = begruendung.split("|", 1)[0];
    if (_DETERMINISTIC_SV_STATUS_RE.test(firstChunk)) {
      return true;
    }
  }
  return false;
}


/**
 * Iter 19.73 (Bug 1) — Zentrale Prüfung „Braucht diese Row eine
 * Gleichwertigkeits-Prüfung durch den Sachbearbeiter?".
 *
 * Vorher wurde nur auf `isAngestelltPosition(taetigkeit)` geprüft — mit
 * dem Ergebnis, dass anstellungstyp-ambivalente Tätigkeiten (z.B.
 * „Reinigungskraft", „Sachbearbeiter", „Analog Circuit Simulation") ODER
 * Backend-ANNAHME-Downgrades (`_auto_fix_iter102=true`) den bernstein-
 * Border/Prüfen-Chip verloren. Das ist paradox, weil gerade ANNAHME-
 * Zeilen am dringendsten eine Sachbearbeiter-Prüfung brauchen.
 *
 * Iter 19.74 (Baustein C) — CV-Quelle immer in Review einbeziehen, wenn
 * die KV eine Gleichwertigkeitsprüfung verlangt. Reine SV-Auszug-Zeilen
 * werden weiterhin ohne bernstein-Marker gerendert (dort ist die
 * Klassifikation deterministisch aus dem Behörden-Dokument).
 *
 * Neue Bedingung — Marker wird gesetzt wenn:
 *   1. KV verlangt Gleichwertigkeits-Prüfung UND
 *   2. Row ist anrechenbar UND
 *   3. Eine dieser Tätigkeits-/Quellen-Charakteristiken:
 *        (a) Explizit „Angestellten"-Position (isAngestelltPosition)
 *        (b) Anstellungstyp-ambivalente Tätigkeit (isAnstellungstypAmbivalentTaetigkeit)
 *        (c) Backend hat auf ANNAHME herabgestuft (`_auto_fix_iter102`)
 *        (d) Zeile stammt aus Lebenslauf/mixed/other (Iter 19.74 Baustein C)
 *
 * Nicht-Anrechenbare Zeilen (Studium, Krankengeld ohne Dienstgeber etc.)
 * bleiben ausgeschlossen — sie werden ohnehin nicht angerechnet und
 * brauchen keinen Gleichwertigkeits-Check.
 */
export function needsEquivalenceReview(row, requiresEquivalenceCheck) {
  if (!row) return false;
  if (requiresEquivalenceCheck !== true) return false;
  if (row.anrechenbar !== true) return false;
  // Iter 20.03.P5-fix (Punkt aus Martina-Fall-4-Report Runde 2) —
  // Praktikum mit Anstellungstyp-Wahl braucht KEINE Gleichwertigkeits-
  // Prüfung mehr. Die KV-Praktikum-Regel greift deterministisch (der
  // User hat explizit gewählt). Ohne diese Ausnahme würde die Zeile
  // trotz Typ-Wahl weiterhin amber-bordered bleiben.
  if (
    row.praktikum_anstellungstyp === "angestellter" ||
    row.praktikum_anstellungstyp === "arbeiter"
  ) {
    return false;
  }
  // Iter 19.75 — Deterministische SV-Statuswörter oder DIRECT/PARTIAL-Matches
  // brauchen keine Gleichwertigkeits-Prüfung (Zivildienst, Karenz, ...
  // sind rechtlich eindeutig, unabhängig von Fachrichtung).
  const _mt = String(row._kv_match_type || "").toLowerCase();
  if (_mt === "direct" || _mt === "partial") return false;
  if (isDeterministicSvStatus(
    row.taetigkeit || "",
    row.anrechenbarkeit_begruendung || "",
  )) return false;

  const taet = row.taetigkeit || "";
  if (isAngestelltPosition(taet)) return true;
  if (isAnstellungstypAmbivalentTaetigkeit(taet)) return true;
  if (row._auto_fix_iter102 === true) return true;
  // Iter 19.74 (Baustein C) — CV-Quelle (oder unbekannt): immer als
  // Review-Kandidat markieren, wenn KV Gleichwertigkeit verlangt.
  const sk = String(row._source_kind || "").toLowerCase();
  if (sk === "lebenslauf" || sk === "mixed" || sk === "other" || sk === "") {
    return true;
  }
  return false;
}

/**
 * Iter 19.63.68 — Erkennt Tätigkeiten, die ein Praktikum enthalten.
 *
 * Hintergrund: KV-Regelwerke werten viele Praktika standardmäßig als
 * anrechenbares Dienstverhältnis (Faktor 1.0). Ein Praktikum ist aber
 * nicht IMMER ein echtes anrechenbares Dienstverhältnis — manche Pflicht-
 * Praktika während der Schul-/Studienzeit zählen nicht. Wir zeigen
 * deshalb einen Hinweis, damit der User die Voreinstellung explizit
 * überprüft.
 *
 * Regex-Design:
 *  • Akzeptiert: „Praktikum", „Praktika", „Praktikumsstelle",
 *    „Ferialpraktikum", „Pflichtpraktikum", „Berufspraktikum".
 *  • Lehnt ab: „Praktikabilität", „praktikabel", „Praktikability"
 *    via Negative-Lookahead `(?!b)` nach „praktika".
 */
export function isPraktikumPosition(taetigkeit) {
  if (!taetigkeit || typeof taetigkeit !== "string") return false;
  return /praktik(?:um|a(?!b))/i.test(taetigkeit);
}

/**
 * Iter 19.63.74 — Praktikum-Anstellungs-Typ Helpers
 *
 * Praktika kommen aus Lebensläufen oft ohne explizite Angabe, ob sie als
 * Angestellten- oder Arbeiterverhältnis geführt wurden. Der User muss
 * das in Schritt 3 aktiv wählen. Wir mappen die Wahl auf das `taetigkeit`-
 * Feld (Prefix „Angestellter — …" / „Arbeiter — …") so dass die bestehende
 * KV-Regel-Pipeline (`applyKvFactorRule`, `match_kv_factor` im Backend)
 * den richtigen Faktor automatisch findet. KEINE Math-Änderung.
 *
 * Datenmodell: optionales Feld `row.praktikum_anstellungstyp` mit Werten
 *   "angestellter" | "arbeiter" | undefined
 *
 * undefined  → User hat noch nicht gewählt → Banner & Inline-Selector aktiv
 * gesetzt    → Faktor & Tätigkeit & Begründung wurden auto-befüllt
 */
const PRAKTIKUM_TYP_PREFIX_RX = /^(?:angestellter|angestellte|arbeiter)\s+—\s+/i;

/** Original-Praktikum-Bezeichnung ohne den auto-gesetzten Anstellungs-Typ-Prefix. */
export function stripPraktikumTypPrefix(taetigkeit) {
  if (!taetigkeit || typeof taetigkeit !== "string") return "";
  return taetigkeit.replace(PRAKTIKUM_TYP_PREFIX_RX, "").trim();
}

/** True wenn die Zeile ein Praktikum ist UND der Anstellungs-Typ noch
 *  nicht gewählt wurde. Wird für den Banner (StepThree) + ANR-Gating
 *  in der Row-Anzeige (VordienstRow) genutzt. */
export function hasUnresolvedPraktikum(row) {
  if (!row || !row.anrechenbar) return false;
  if (!isPraktikumPosition(row.taetigkeit)) return false;
  const typ = row.praktikum_anstellungstyp;
  return typ !== "angestellter" && typ !== "arbeiter";
}

/**
 * Iter 19.68.3 — Eine Vordienst-Row hat eine UNGEPRÜFTE Quality-Warning,
 * wenn das Backend `_quality_flags` gesetzt hat UND der User die Position
 * weder manuell bearbeitet (`_quality_ack`=true durch onChange-Wrapper)
 * noch die „Geprüft & bestätigt"-Checkbox aktiviert hat.
 *
 * Wird verwendet für:
 *   • Linken amber Border-Stripe am VordienstRow-Card
 *   • Hinweisbox „N Positionen ungeprüft" oben in Step 3
 *   • Warning-Banner in Step 4 + PDF-Sektion „Ignorierte Hinweise"
 *
 * Auto-Korrekturen (`_auto_corrected`) zählen NICHT als ungeprüfte
 * Warnung — das Backend hat dort bereits eine sichere Doppel-Quellen-
 * Korrektur vorgenommen, die nur informativ angezeigt wird.
 */
export function hasUnresolvedQualityWarning(row) {
  if (!row) return false;
  const flags = Array.isArray(row._quality_flags) ? row._quality_flags : [];
  if (flags.length === 0) return false;
  return row._quality_ack !== true;
}

/**
 * Iter 19.68.7 — Sammel-Indikator „Diese Position braucht noch User-Input".
 *
 * Kombiniert die drei orthogonalen Prüf-Trigger zu einem einzigen Boolean,
 * der für Filter-Chips (Step 3 „Prüfen") und ggf. weitere Audit-Banner
 * verwendet wird:
 *   1. Quality-Warning ungeprüft (`hasUnresolvedQualityWarning`)
 *   2. Praktikum-Typ unentschieden (`hasUnresolvedPraktikum`)
 *   3. Gleichwertigkeit zu prüfen (KV verlangt es UND row.anrechenbar UND
 *      Tätigkeit ist Angestellte-Position) — nur dann ist die Prüfung
 *      relevant; bei Nicht-Anrechenbarkeit oder Arbeiter-Position wäre
 *      es ein irrelevanter Warn-Flood.
 *
 * `opts.requiresEquivalenceCheck`: KV-weiter Flag (Boolean). Wenn nicht
 * angegeben → Gleichwertigkeit fällt aus der Bewertung raus.
 */
export function rowNeedsAttention(row, opts = {}) {
  if (!row) return false;
  // Iter 20.36.f14.22 — User-„Geprüft"-Toggle short-circuit: wenn der
  // Sachbearbeiter die Zeile bereits abgehakt hat, fällt sie aus der
  // „Prüfen"-Zählung raus (und damit auch aus dem gleichnamigen Filter).
  // Rein Frontend-only Flag (`_review_verified` via `mapBackendItem`).
  if (row._review_verified === true) return false;
  if (hasUnresolvedQualityWarning(row)) return true;
  if (hasUnresolvedPraktikum(row)) return true;
  // Iter 19.73 (Bug 1) — Gleichwertigkeit auch bei ANNAHME/ambivalenten
  // Tätigkeiten anzeigen, nicht nur bei explizit „Angestellter"-Text.
  if (needsEquivalenceReview(row, opts.requiresEquivalenceCheck === true)) {
    return true;
  }
  // Iter 20.36.f14.18 — Präsenzdienst-Regel (KV-Sonderregel) verlangt
  // deterministische Sachbearbeiter-Verifikation (Umschließung durch
  // Dienst-/Lehrverhältnis). Damit die „Prüfen"-Filterchip-Zahl solche
  // Zeilen korrekt zählt.
  if (row._praesenzdienst_needs_review === true) return true;
  // Iter 20.36.f14.18 — „Laufend"-Zeilen: Enddatum offen → Sachbearbeiter
  // muss auf Aktualität prüfen (Stichtag-1-Berechnung setzt heutigen Tag
  // ein; wenn die Position inzwischen beendet ist, ist das Datum falsch).
  if (row.was_laufend === true) return true;
  return false;
}



/**
 * Mutator: setzt `praktikum_anstellungstyp` + passt `taetigkeit`,
 * `anrechnungsfaktor`, `anrechenbarkeit_begruendung` deterministisch an.
 *
 * Args:
 *   row              — das aktuelle Row-Objekt
 *   typ              — "angestellter" | "arbeiter"
 *   faktorRegeln     — selectedKv.faktor_regeln (Fallback-Match auf generische
 *                      „Angestellter"/„Arbeiter"-Keywords)
 *   praktikumRegeln  — selectedKv.praktikum_regeln (Iter 19.63.75) — KV-
 *                      spezifische Faktoren pro Anstellungstyp. Hat VORRANG
 *                      vor faktorRegeln, da Praktika in manchen KVs explizit
 *                      anders gewertet werden (z.B. Arbeiter-Praktikum voll,
 *                      Angestellten-Praktikum hälftig).
 *
 * Returns: neues Row-Objekt (immutable). Cache-Felder werden invalidiert,
 * damit der Faktor sofort in der Live-Preview greift (siehe Iter 19.63.67).
 */
export function setPraktikumTyp(row, typ, faktorRegeln, praktikumRegeln) {
  if (!row || (typ !== "angestellter" && typ !== "arbeiter")) return row;
  const cleanBezeichnung = stripPraktikumTypPrefix(row.taetigkeit || "");
  const typLabel = typ === "angestellter" ? "Angestellter" : "Arbeiter";
  const newTaetigkeit = cleanBezeichnung
    ? `${typLabel} — ${cleanBezeichnung}`
    : typLabel;

  // ── Iter 19.63.75 — Vorrang: KV-spezifische Praktikum-Regel ──────────
  // Iter 19.63.86 — Faktor + anrechenbar werden deterministisch aus der
  // KV-Regel abgeleitet. Die `anrechenbarkeit_begruendung` wird hier
  // jedoch NICHT MEHR überschrieben (vorher: technische Floskel
  // „[Faktor 1 gesetzt gemäß KV-Regel …]" überschrieb den LLM-Hinweis).
  // Der ursprüngliche LLM-Text bleibt unverändert; die Anstellungs-Typ-
  // Wahl ist sichtbar über die Status-Zeile „✓ Praktikum als X eingestuft"
  // sowie über das dynamische „ANGESTELLTER"/„ARBEITER"-Badge.
  let faktor;
  let anrechenbar;
  const pr = praktikumRegeln || {};
  if (typeof pr[typ] === "number") {
    faktor = pr[typ];
    anrechenbar = faktor > 0;
  } else {
    // ── Fallback: generische KV-Faktor-Regel via Tätigkeits-Prefix ──
    const rule = applyKvFactorRule(newTaetigkeit, faktorRegeln || {});
    faktor = rule ? rule.faktor : 1.0;
    anrechenbar = rule ? rule.anrechenbar : true;
  }
  return {
    ...row,
    praktikum_anstellungstyp: typ,
    taetigkeit: newTaetigkeit,
    anrechenbar,
    anrechnungsfaktor: faktor,
    // Iter 19.63.86 — Begründungstext bewusst NICHT überschrieben.
    // anrechenbarkeit_begruendung: row.anrechenbarkeit_begruendung (unverändert).
    // Backend-Cache invalidieren — Math-Wert muss vom Backend neu berechnet
    // werden bei nächstem „Summe aktualisieren".
    months_anrechenbar: undefined,
    effective_days_anrechenbar: null,
    dauer_anrechenbar_jahre: undefined,
    dauer_anrechenbar_monate: undefined,
    dauer_anrechenbar_tage: undefined,
    display_raw: undefined,
    display_anrechenbar: undefined,
    effective_period: undefined,
    display_anrechenbar_pre_cap: undefined,
    dauer_anrechenbar_pre_cap_jahre: undefined,
    dauer_anrechenbar_pre_cap_monate: undefined,
    dauer_anrechenbar_pre_cap_tage: undefined,
  };
}

/** Parse "YYYY-MM" or "YYYY-MM-DD" to a JS Date (UTC noon to avoid DST). null on fail. */
function parseDateStr(s) {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec((s || "").trim());
  if (!m) return null;
  const y = +m[1], mo = +m[2], d = m[3] ? +m[3] : 1;
  const dt = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Last day of given (year, month) — 1-based month. */
function lastDayOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Resolve the bis-string (or 'laufend' / '') to a concrete Date object,
 *  using stichtag (or today) as fallback. Returns null if unparseable.
 *
 *  Iter 19.63.24 — Für „laufend"/leer/„offen" wird der Stichtag MINUS 1 TAG
 *  verwendet, weil der Stichtag selbst der erste Tag im neuen Job ist und
 *  daher die laufende Vorbeschäftigung dort schon nicht mehr angerechnet
 *  werden darf. Konsistent mit Backend (`_parse_period_to_months`).
 */
function resolveBisDate(bis, stichtag) {
  const bStr = (bis || "").trim().toLowerCase();
  if (!bStr || bStr === "laufend" || bStr === "offen") {
    const stMatch = stichtag
      ? /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(String(stichtag).trim())
      : null;
    if (stMatch) {
      const sy = +stMatch[1], smo = +stMatch[2];
      const sd = stMatch[3] ? +stMatch[3] : lastDayOfMonth(sy, smo);
      // Stichtag − 1 Tag (12:00 UTC, um DST-Drifts zu vermeiden).
      const dt = new Date(Date.UTC(sy, smo - 1, sd, 12, 0, 0));
      dt.setUTCDate(dt.getUTCDate() - 1);
      return dt;
    }
    const today = new Date();
    const dt = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0));
    dt.setUTCDate(dt.getUTCDate() - 1);
    return dt;
  }
  const m2 = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(bStr);
  if (!m2) return null;
  const y2 = +m2[1], mo2 = +m2[2], d2 = m2[3] ? +m2[3] : lastDayOfMonth(y2, mo2);
  return new Date(Date.UTC(y2, mo2 - 1, d2, 12, 0, 0));
}

/** Fractional months between von (inclusive) and bis (inclusive), day-precision.
 *  'laufend' / '' / 'offen' → bis = stichtag (or today). */
export function monthsBetween(von, bis, stichtag) {
  const start = parseDateStr(von);
  if (!start) return 0;
  const end = resolveBisDate(bis, stichtag);
  if (!end || end < start) return 0;
  const days = Math.round((end - start) / 86400000) + 1; // include both endpoints
  return days / 30.4375;
}

/**
 * Decompose the difference between `start` (inclusive) and `endEx` (exclusive)
 * into {years, months, days} using relativedelta-style borrowing.
 *
 * Caller is responsible for converting "inclusive end" → "exclusive end"
 * (add 1 day) before passing in, so this helper stays a pure delta-of-two-
 * dates calculator. Used by `diffYMD` and `effectiveDurationYMD`.
 */
/**
 * Python's `round()`-style banker's rounding (half-to-even). Used so the
 * frontend aggregator's per-item rounding matches the Python backend's,
 * to the bit. Plain `Math.round` is half-UP and diverges on .5-values
 * (e.g. round(68.5) = 69 in JS but 68 in Python).
 *
 * Iter 19.63.28 — `_bankerRound` wurde mit `aggregateHybridYMD` entfernt.
 * Falls künftig wieder benötigt, hier ist die Referenz:
 *   diff < 0.5 → floor; > 0.5 → ceil; === 0.5 → round to even.
 */

/**
 * Decompose a calendar-day delta into Y/M/T using Python `relativedelta`
 * semantics: years/months are first computed as the largest integers that,
 * when added to `start` (with day-of-month clamping), still stay ≤ `endEx`.
 * Remainder is the day-difference between that anchored date and `endEx`.
 *
 * Iter 19.59 — Pre-19.59 used a "previous month's last day"-fallback that
 * diverged from `relativedelta` by 1 d when start.day was greater than the
 * shorter month's day count (e.g. start=29.09 → end=26.03 → result must be
 * 5 M 26 T, not 5 M 25 T).
 */
function _decomposeDelta(start, endEx) {
  let years = endEx.getUTCFullYear() - start.getUTCFullYear();
  let months = endEx.getUTCMonth() - start.getUTCMonth();
  if (endEx.getUTCDate() < start.getUTCDate()) {
    months -= 1;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  // Anchor: start + years + months, day clamped to target-month length.
  // Anchor MUST use the same time-of-day as start/endEx (see parseDateStr,
  // which uses 12:00 UTC); otherwise rounding 0.5 → next integer drift.
  const targetY = start.getUTCFullYear() + years + Math.floor((start.getUTCMonth() + months) / 12);
  const targetM = ((start.getUTCMonth() + months) % 12 + 12) % 12;
  const lastDay = new Date(Date.UTC(targetY, targetM + 1, 0)).getUTCDate();
  const clampedDay = Math.min(start.getUTCDate(), lastDay);
  const anchor = new Date(Date.UTC(targetY, targetM, clampedDay, 12, 0, 0));
  const days = Math.round((endEx - anchor) / 86400000);
  return { years, months, days };
}

/**
 * Calendar-correct decomposition of (von, bis) into {years, months, days, totalDays}.
 * Uses the "end + 1 day" trick to make endpoints inclusive — matches the
 * Austrian labor-law convention where 11.03.2013 to 31.01.2015 is exactly
 * 1 year, 10 months, 21 days (not 23 days as the 30.4375-approximation suggests).
 */
export function diffYMD(von, bis, stichtag) {
  const start = parseDateStr(von);
  if (!start) return { years: 0, months: 0, days: 0, totalDays: 0 };
  const endInclusive = resolveBisDate(bis, stichtag);
  if (!endInclusive || endInclusive < start) return { years: 0, months: 0, days: 0, totalDays: 0 };

  const endEx = new Date(endInclusive.getTime() + 86400000);
  const { years, months, days } = _decomposeDelta(start, endEx);
  const totalDays = Math.round((endInclusive - start) / 86400000) + 1;
  return { years, months, days, totalDays };
}

/**
 * For a row with reduced anrechenbar months (due to factor < 1, overlap,
 * or cap): lay out `monthsEffective × 30.4375` days starting from von and
 * compute a calendar-aware Y/M/T decomposition. Used for the "↳ X anr."
 * sub-line in the UI.
 */
export function effectiveDurationYMD(von, monthsEffective) {
  if (!monthsEffective || monthsEffective <= 0) {
    return { years: 0, months: 0, days: 0, totalDays: 0 };
  }
  const start = parseDateStr(von);
  const effDays = Math.round(monthsEffective * 30.4375);
  if (!start) return { years: 0, months: 0, days: effDays, totalDays: effDays };
  // Effective end (exclusive) = start + effDays days.
  const endEx = new Date(start.getTime() + effDays * 86400000);
  const { years, months, days } = _decomposeDelta(start, endEx);
  return { years, months, days, totalDays: effDays };
}

/**
 * Iter 19.63.73 — 30/360-konforme Live-Preview-Helper. Spiegeln die
 * Backend-Math (`_compute_row_dauer` + `_normalize_days_to_ymd`) bit-genau
 * im Frontend, damit der Live-Preview nach manuellen Faktor-/Tätigkeit-
 * Edits NICHT mehr durch Kalender↔30/360-Roundtrip-Drifts verfälscht wird.
 *
 * Beispiel-Drift (vorher mit `monthsBetween` + `effectiveDurationYMD`):
 *   01.01.1997–31.12.2002 (= 6 J. roh = 2160 d 30/360)
 *   × Faktor 0.5  → eigentlich exakt 1080 d 30/360 = 3 J.
 *   Calendar-Pfad: 2191 d / 30.4375 = 71.984 mo × 0.5 = 35.992 mo
 *                  × 30.4375 = 1095.4998 → Math.round half-up = 1096 d
 *                  → "3 J. 1 T."  ← FALSCH (FE-Drift, Backend wäre 3 J.)
 *
 * Backend-konform (30/360):
 *   Roh-Y/M/T = 6 J. 0 M. 0 T. → roh_days_30360 = 6*360 + 0*30 + 0 = 2160
 *   × 0.5 = 1080 → divmod(1080, 360) = (3, 0) → (0, 0) → "3 J." ✅
 */

/** Liefert fraktionale 30/360-Monate (Y*12 + M + D/30) — matcht Backend. */
export function monthsBetween30360(von, bis, stichtag) {
  const { years, months, days } = diffYMD(von, bis, stichtag);
  return years * 12 + months + days / 30;
}

/**
 * Y/M/T-Decomposition für `monthsEffective` (= Roh-Monate × Faktor) im
 * strikten 30/360-Pfad. Spiegelt Backend `_normalize_days_to_ymd`:
 *   total = round(days_float, half-up)
 *   y, rest = divmod(total, 360)
 *   m, d    = divmod(rest, 30)
 *
 * JS `Math.round` ist half-up wie Python's `int(x + 0.5)` — passt.
 */
export function effectiveDurationYMD30360(monthsEffective) {
  if (!monthsEffective || monthsEffective <= 0) {
    return { years: 0, months: 0, days: 0, totalDays: 0 };
  }
  const daysFloat = monthsEffective * 30; // 30/360-Tage
  const total = Math.round(daysFloat);
  const years = Math.floor(total / 360);
  const rest = total % 360;
  const months = Math.floor(rest / 30);
  const days = rest % 30;
  return { years, months, days, totalDays: total };
}

/** Format a Y/M/T object as compact German text: "1 J. 10 M. 21 T."
 *  Iter 19.43.8 — Tage werden IMMER angezeigt, wenn > 0 (User-Spec: keine
 *  Rundung der „Restkrümel"). Vorher wurden Tage < 5 weggelassen, was zu
 *  irritierenden Anzeigen wie „10 M." statt „10 M. 4 T." führte. */
export function formatYMD(d) {
  if (!d) return "0 T.";
  const y = d.years || 0, m = d.months || 0, t = d.days || 0;
  if (!y && !m && !t) return "0 T.";
  const parts = [];
  if (y) parts.push(`${y} J.`);
  if (m) parts.push(`${m} M.`);
  if (t > 0) parts.push(`${_fmtNumDE(t)} T.`);
  return parts.length ? parts.join(" ") : "0 T.";
}

/**
 * Format a numeric value with German decimal comma. Whole numbers stay
 * integer (no trailing ",0"); half-days render as e.g. "25,5".
 * Iter 19.62 — per-row halves are now passed through unrounded.
 */
function _fmtNumDE(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return String(n ?? "");
  if (Number.isInteger(n)) return String(n);
  // Round to one decimal to avoid float-noise like 25.499999999
  const rounded = Math.round(n * 10) / 10;
  if (Number.isInteger(rounded)) return String(rounded);
  return String(rounded).replace(".", ",");
}

// Iter 19.63.32 — `formatYearsMonths` (alias for `formatDuration`) wurde
// entfernt; alle Aufrufer nutzen jetzt direkt `formatDuration` oder die
// authoritative Backend-`display`-Strings.

// ─── Iter 19.42 — Kaufmännische Dauer 30/360 ────────────────────────────────
//
// User-Spec 2026-05-17 (Variante Hybrid + 30/360):
//
//   Schritt 1: pro Zeile Roh-Y/M/D via relativedelta(end+1, start) — kalendarisch
//              genau, inklusiv-Behandlung des Enddatums.
//   Schritt 2: effective_days = Y×360 + M×30 + D — multipliziert mit
//              anrechnungsfaktor (1.0 / 0.5 / 0.0). Iter 19.42.2: 360 statt 365.
//   Schritt 3: Normalisierung: Tage/30 → Monate (Rest=Tage), Monate/12 → Jahre.
//              Hybrid-Logik: Faktor=1.0 ohne Loss umgeht Schritt 2 ganz.
//
// Beispiele:
//   05.02.2018 – 04.02.2019           → 1 J / 0 M / 0 T   (Roh-effective=360 d)
//   04.08.2018 – 03.09.2018           → 0 J / 1 M / 0 T   (Roh-effective=30 d)
//   1 Roh-J × Faktor 0.5              → 180 d → 0 J / 6 M / 0 T

/** Compute raw (calendar-accurate) Y/M/D for a single row PLUS the effective
 *  days = Y×360 + M×30 + D (Schritt 1 + 2 ohne Faktor).
 */
/** Iter 19.68.6 — Konsolidierungs-Lücke berücksichtigen.
 *
 *  Bei konsolidierten Items (mehrere Source-Items mit Lücke ≤ 1 Tag
 *  zusammengefasst) wird die Lücke in `_consolidation_gap_days` getrackt
 *  und MUSS vom Brutto-effective_days abgezogen werden, sonst zählt die
 *  Lücke (nicht-anrechenbarer Tag) fälschlich zur Beschäftigung.
 *
 *  Beispiel:
 *    P1: 20.12.–21.12. (2 T.) + P2: 23.12.–23.12. (1 T.), Lücke = 22.12.
 *    Konsolidiert: 20.12.–23.12. (4 T. brutto) − 1 T. Lücke = 3 T. korrekt.
 */
export function computeRowDauer(von, bis, stichtag, consolidationGapDays = 0) {
  const ymd = diffYMD(von, bis, stichtag);
  let effectiveDays =
    (ymd.years || 0) * 360 + (ymd.months || 0) * 30 + (ymd.days || 0);
  let y = ymd.years || 0;
  let m = ymd.months || 0;
  let d = ymd.days || 0;
  const gap = Math.max(0, Number(consolidationGapDays) || 0);
  if (gap > 0 && effectiveDays > 0) {
    const corrected = Math.max(0, effectiveDays - Math.round(gap));
    if (corrected !== effectiveDays) {
      const norm = normalizeDaysToYMD(corrected);
      y = norm.years;
      m = norm.months;
      d = norm.days;
      effectiveDays = corrected;
    }
  }
  return {
    jahre: y,
    monate: m,
    tage: d,
    effectiveDays,
  };
}

/** Schritt 3 — kaufmännische Normalisierung 30/360. Returns ganze Tage. */
export function normalizeDaysToYMD(daysFloat) {
  const total = Math.max(0, Math.floor((Number(daysFloat) || 0) + 0.5));
  const y = Math.floor(total / 360);
  const restAfterYears = total % 360;
  const m = Math.floor(restAfterYears / 30);
  const d = restAfterYears % 30;
  return { years: y, months: m, days: d, totalDays: total };
}

/** Format effective days as "X J. Y M. Z T." — pivot from formatDuration when
 *  the upstream value comes in days (Iter 19.42). */
export function formatDurationDays(daysFloat) {
  const { years, months, days } = normalizeDaysToYMD(daysFloat);
  if (!years && !months && !days) return "0 T.";
  const parts = [];
  if (years > 0) parts.push(`${years} J.`);
  if (months > 0) parts.push(`${months} M.`);
  if (days > 0 || (!years && !months)) parts.push(`${days} T.`);
  return parts.join(" ");
}

/** Sum effective anrechenbare days across all rows (Iter 19.42). Prefer
 *  the backend-computed `effective_days_anrechenbar` if present (already
 *  factor + overlap + cap applied). Fallback: compute from Y/M/D × factor.
 */
export function sumAnrechenbareDays(items, stichtag) {
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum, it) => {
    if (!it || !it.anrechenbar) return sum;
    if (typeof it.effective_days_anrechenbar === "number") {
      return sum + it.effective_days_anrechenbar;
    }
    // Fallback for manually-added rows that haven't been processed yet:
    // compute Roh-Days × factor.
    const { effectiveDays } = computeRowDauer(it.datum_von, it.datum_bis, stichtag);
    const f = typeof it.anrechnungsfaktor === "number" ? it.anrechnungsfaktor : 1.0;
    return sum + effectiveDays * f;
  }, 0);
}

/** Sum Ausbildungs-rows in effective days. Ausbildungs-`months` are
 *  ungerichtete fix-credits (z.B. HAK-Matura = 24 M = 720 d).
 */
export function sumAusbildungDays(rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.reduce((s, r) => {
    if (!r || !r.anrechenbar) return s;
    const m = Number(r.months) || 0;
    return s + Math.max(0, m * 30);
  }, 0);
}

/** Iter 19.63.31 — Single Mapping Point.
 *
 *  Backend liefert Vordienstzeit-Items mit vielen optionalen Feldern
 *  (`display_anrechenbar`, `dauer_anrechenbar_*`, `was_laufend`, …).
 *  Mehrere Step-3-Codepfade (Extract, Review, Manual-Review) müssen
 *  diese in den lokalen React-State mappen. Vorher waren 3 manuelle
 *  Map-Stellen — leicht zu vergessen wenn ein neues Backend-Feld
 *  hinzukommt (siehe Iter 19.63.30 Display-Drift-Bug).
 *
 *  Jetzt: EIN zentraler Mapper. Bei künftigen Backend-Feld-Erweiterungen
 *  nur HIER eine Zeile hinzufügen.
 *
 *  @param {object} it - Roh-Item aus Backend-Response
 *  @param {object} [fallback] - Optionales Fallback-Item (z.B. rawItems[idx]
 *    bei Review-Response, falls Backend ein optionales Feld nicht zurückgibt)
 *  @returns {object} - Item für React-State (alle bekannten Felder)
 */
export function mapBackendItem(it, fallback = {}) {
  if (!it || typeof it !== "object") return null;
  const numOr = (val, fallbackVal) =>
    typeof val === "number" ? val : fallbackVal;
  const strOr = (val, fallbackVal) =>
    typeof val === "string" ? val : fallbackVal;
  const arrOr = (val, fallbackVal) =>
    Array.isArray(val) ? val : fallbackVal;
  // Iter 19.63.77 — Praktikum-Frontend-Normalisierung.
  // Selbst wenn der LLM defensiv `anrechenbar=false` für ein Praktikum
  // ausspuckt (z.B. „Anstellungstyp unklar" oder „nicht handelsspezifisch"),
  // ÜBERSCHREIBEN wir das hier deterministisch: ein Praktikum wird IMMER
  // initial als „pending user choice" markiert (anrechenbar=true,
  // anrechnungsfaktor=1.0 Placeholder, praktikum_anstellungstyp=undefined).
  // Der Sachbearbeiter trifft die Wahl Angestellter/Arbeiter in Schritt 3,
  // danach setzt `setPraktikumTyp` den Faktor gemäß KV-Praktikum-Regel.
  // Die LLM-Begründung bleibt als Audit-Trail erhalten.
  //
  // Iter 19.63.78 — Persistierung der User-Wahl über Backend-Roundtrip:
  // Backend kennt das Feld `praktikum_anstellungstyp` nicht und reicht es
  // bei `POST /api/tool/recalc-vordienstzeiten` nicht zurück. Ohne Inferenz
  // würde `mapBackendItem` dann fälschlich auf Override schalten und die
  // User-Wahl mit dem Initial-Default überschreiben (siehe Bug-Report
  // Martina-Fall-4). Lösung: Infer den Anstellungstyp deterministisch
  // aus dem Tätigkeits-Prefix („Arbeiter — …" / „Angestellter — …"),
  // den `setPraktikumTyp` beim User-Klick gesetzt hat. Damit überlebt
  // die Wahl jeden Roundtrip.
  const taetigkeit = it.taetigkeit || "";
  const llmAnr = Boolean(it.anrechenbar);
  const llmFaktor = numOr(it.anrechnungsfaktor, 1.0);
  const isPraktikumRow = isPraktikumPosition(taetigkeit);
  // Iter 20.16 — KV-Auto-Exklusion für Praktika. Wenn das Backend
  // signalisiert, dass der KV Praktika grundsätzlich nicht anrechnet
  // (praktikum_regeln={0,0}), respektieren wir das und behandeln die
  // Zeile wie einen normalen nicht-anrechenbaren Eintrag — kein Pending-
  // Zustand, kein Auto-Override auf anrechenbar=true, keine Choice-Buttons.
  const isKvPraktikumAusgeschlossen = Boolean(it._kv_praktika_ausgeschlossen);
  const _PRAKTIKUM_PREFIX_RX = /^(Angestellter|Angestellte|Arbeiter)\s+—\s+/i;
  const _prefixMatch = _PRAKTIKUM_PREFIX_RX.exec(taetigkeit);
  const inferredTyp = _prefixMatch
    ? (_prefixMatch[1].toLowerCase().startsWith("arbeit") ? "arbeiter" : "angestellter")
    : undefined;
  const finalTyp =
    it.praktikum_anstellungstyp === "angestellter" || it.praktikum_anstellungstyp === "arbeiter"
      ? it.praktikum_anstellungstyp
      : inferredTyp;
  const hasUserChoice = finalTyp === "angestellter" || finalTyp === "arbeiter";
  // Iter 20.03.P5-fix Runde 3 (Martina-Fall-4-Report Runde 3, Bugs 2+3) —
  // Praktikum-Anrechnen-Toggle darf durch den defensiven Backend-Override
  // NICHT überschrieben werden, sonst passiert Folgendes:
  //   (a) User toggelt „Anrechnen" auf AUS → State `anrechenbar=false`
  //   (b) `useDebouncedAutoRecalc` feuert nach 400 ms Recalc-Roundtrip
  //   (c) Backend antwortet mit `anrechenbar=true` (LLM-Praktikum-Regel,
  //       da User im Backend nichts geändert hat)
  //   (d) `mapBackendItem` ohne Fix: overridePraktikum=true → State-Reset
  //       auf `anrechenbar=true` (Toggle springt zurück, User sieht nichts)
  //   (e) State-Diff → nächster Recalc → ENDLOSSCHLEIFE
  //
  // Fix: Wenn der Frontend-State (`fallback`) bereits einen expliziten
  // `anrechenbar`-Boolean-Wert enthält (= Recalc/Review-Roundtrip mit
  // vorheriger User-Interaktion), gewinnt der State-Wert über den Override.
  // Nur beim allerersten Extract-Call (fallback = {}) greift der Praktikum-
  // Override, damit ein LLM-Miss (`anrechenbar=false` bei einem Praktikum)
  // ohne User-Wahl korrigiert wird.
  //
  // Der Faktor-Placeholder (1.0) bleibt für Praktikum-Rows ohne User-Wahl
  // bestehen, unabhängig vom Anrechenbar-Toggle — sonst könnte das LLM
  // dort einen 0.5- oder 0.0-Faktor eingeschleust haben, der die spätere
  // KV-Praktikum-Regel-Berechnung verfälschen würde.
  // Iter 20.16 — Wenn KV-Auto-Exklusion aktiv ist, ist die Zeile nicht
  // „pending" — der Backend-Zustand (anrechenbar=false, faktor=0) darf
  // durchreichen. User kann per manuellem Toggle overriden wie bei jeder
  // anderen nicht-anrechenbaren Zeile.
  const isPraktikumPending = isPraktikumRow && !hasUserChoice && !isKvPraktikumAusgeschlossen;
  const hasStateAnr = typeof fallback.anrechenbar === "boolean";
  const isAnr = isPraktikumPending && !hasStateAnr ? true : (hasStateAnr ? fallback.anrechenbar : llmAnr);
  const faktor = isPraktikumPending ? 1.0 : llmFaktor;
  const overridePraktikum = isPraktikumPending;
  const llmBegr = it.anrechenbarkeit_begruendung || "";
  const begruendung = overridePraktikum
    ? (llmBegr
        ? `Praktikum erkannt — Anstellungs-Typ (Angestellter/Arbeiter) in Schritt 3 wählen.\n\nHinweis aus Auswertung: ${llmBegr}`
        : "Praktikum erkannt — Anstellungs-Typ (Angestellter/Arbeiter) in Schritt 3 wählen. Anrechnungsfaktor wird laut KV-Praktikum-Regel automatisch gesetzt.")
    : llmBegr;
  // Iter 19.63.69 — Defense-in-Depth gegen ANR-Toggle-Wiederbelebungs-Bug:
  // Bei `anrechenbar=false` liefert das Backend zwar mathematisch korrekte
  // 0-Werte (`dauer_anrechenbar_*=0`, `display_anrechenbar="0 T."`,
  // `months_anrechenbar=0`), aber diese „stalen 0er" haben in der Vergangen-
  // heit den ANR-Wiederbelebungs-Code-Pfad gebrochen (Iter 19.63.67):
  // beim Re-Toggle auf anrechenbar=true blieben die 0-Werte hängen und
  // VordienstRow zeigte „0 T. durchgestrichen" trotz grünem Häkchen.
  //
  // Lösung: Wenn das Backend `anrechenbar=false` sagt, schreiben wir die
  // anrechenbarkeitsabhängigen Cache-Felder GAR NICHT in den State. Dann
  // gibt es keine stalen 0-Werte mehr, und VordienstRow fällt automatisch
  // auf seinen lokalen `monthsEffective = months × faktor`-Pfad zurück.
  // `display_raw` bleibt erhalten (= Gesamtdauer, unabhängig von ANR).
  //
  // Iter 19.63.77 — Praktikum-Override: bei Override muss isAnr=true sein,
  // damit die Display-Felder weitergegeben werden (Faktor ist nur Placeholder,
  // Live-Preview rechnet eh sofort neu).
  return {
    // Identitäts-Felder
    datum_von: it.datum_von || "",
    datum_bis: it.datum_bis || "",
    arbeitgeber: it.arbeitgeber || "",
    taetigkeit,
    // Klassifikation
    anrechenbar: isAnr,
    anrechnungsfaktor: faktor,
    anrechenbarkeit_begruendung: begruendung,
    // Iter 19.63.74 / 19.63.78 — Praktikum-Anstellungstyp. Wenn der User
    // gewählt hat, halten wir entweder den Backend-Wert oder den aus dem
    // Tätigkeits-Prefix inferierten Wert fest, damit die Wahl Backend-
    // Roundtrips überlebt. undefined = User muss erst wählen.
    praktikum_anstellungstyp: hasUserChoice ? finalTyp : undefined,
    // Iter 19.63.21 — „laufend"-Marker für Stichtag-1-Berechnung
    was_laufend: Boolean(it.was_laufend),
    // Iter 19.61.2 / 19.63.27 / 19.63.69 / 19.63.77 — Per-Item Y/M/T +
    // Display-Strings. Nur bei isAnr=true UND ohne Praktikum-Override
    // aus Backend in State schreiben. Bei Override sind die Backend-Werte
    // stale 0er (weil LLM ursprünglich anrechenbar=false sagte) — die
    // Live-Preview rechnet sofort neu mit `monthsBetween30360 × faktor`.
    dauer_anrechenbar_jahre: isAnr && !overridePraktikum ? numOr(it.dauer_anrechenbar_jahre, undefined) : undefined,
    dauer_anrechenbar_monate: isAnr && !overridePraktikum ? numOr(it.dauer_anrechenbar_monate, undefined) : undefined,
    dauer_anrechenbar_tage: isAnr && !overridePraktikum ? numOr(it.dauer_anrechenbar_tage, undefined) : undefined,
    display_raw: strOr(it.display_raw, fallback.display_raw),
    display_anrechenbar: isAnr && !overridePraktikum ? strOr(it.display_anrechenbar, fallback.display_anrechenbar) : undefined,
    // Iter 19.63.33 — Cap-Marker: Item wurde komplett vom Cap aufgezehrt
    // (z.B. spätere Position nach Cap-Erschöpfung). Frontend kann darauf
    // reagieren (z.B. Modal zeigt Pre-Cap-Werte transparent, Sidebar/Step 4
    // nutzen die korrekten 0-Werte).
    _cap_zeroed: Boolean(it._cap_zeroed),
    _overlap_zeroed: Boolean(it._overlap_zeroed),
    // Iter 20.16 — KV-Auto-Exklusion-Marker. Wird vom Backend im
    // Extract-Pfad gesetzt und muss Roundtrips überleben, damit die UI
    // beim Recalc weiterhin keine Choice-Buttons zeigt.
    _kv_praktika_ausgeschlossen: isKvPraktikumAusgeschlossen,
    // Iter 19.63.35 — Partielle Cap-Reduktion (Item nicht 0, aber gekürzt).
    // Modal nutzt diesen Marker, um die Reduktion NICHT als „Überlappung"
    // anzuzeigen (= reine Cap-Reduktion, soll erst in Final-Kaskade transparent
    // werden).
    _cap_reduced: Boolean(it._cap_reduced),
    // Iter 20.36.f9 — WT-Sub-Cap-Marker über Roundtrip erhalten, damit
    // der Modal die entsprechenden Rows im Sum korrekt behandelt und
    // visuell als sub-cap-affected markieren kann. Ohne diese Preservation
    // würden die post-sub-cap-Werte zwar in `dauer_anrechenbar_*` stehen,
    // aber die Modal-Diagnose-Logik (isSubCapAffected) hätte kein Signal.
    _wt_sub_cap_zeroed: Boolean(it._wt_sub_cap_zeroed),
    _wt_sub_cap_partial: Boolean(it._wt_sub_cap_partial),
    // Iter 19.63.34 / 19.63.69 / 19.63.77 — Pre-Cap-Werte. Nur bei
    // isAnr=true UND ohne Praktikum-Override.
    display_anrechenbar_pre_cap: isAnr && !overridePraktikum
      ? strOr(it.display_anrechenbar_pre_cap, undefined)
      : undefined,
    dauer_anrechenbar_pre_cap_jahre: isAnr && !overridePraktikum
      ? numOr(it.dauer_anrechenbar_pre_cap_jahre, undefined)
      : undefined,
    dauer_anrechenbar_pre_cap_monate: isAnr && !overridePraktikum
      ? numOr(it.dauer_anrechenbar_pre_cap_monate, undefined)
      : undefined,
    dauer_anrechenbar_pre_cap_tage: isAnr && !overridePraktikum
      ? numOr(it.dauer_anrechenbar_pre_cap_tage, undefined)
      : undefined,
    // Iter 19.63.4 / 19.63.69 — Math-Trace (Gesamt-Dauer, immer behalten).
    dauer_jahre: numOr(it.dauer_jahre, fallback.dauer_jahre),
    dauer_monate: numOr(it.dauer_monate, fallback.dauer_monate),
    dauer_tage: numOr(it.dauer_tage, fallback.dauer_tage),
    // effective_period + months_anrechenbar + effective_days_anrechenbar
    // sind anrechenbarkeitsabhängig → nur bei isAnr UND nicht-Override.
    effective_period: isAnr && !overridePraktikum ? (it.effective_period || undefined) : undefined,
    months_anrechenbar: isAnr && !overridePraktikum ? numOr(it.months_anrechenbar, undefined) : undefined,
    effective_days_anrechenbar: isAnr && !overridePraktikum
      ? numOr(it.effective_days_anrechenbar, fallback.effective_days_anrechenbar)
      : undefined,
    // Source-Zeilen (pre-Konsolidierung) für Detail-Popover
    source_items: arrOr(it.source_items, fallback.source_items),
    // Iter 19.63.101 — Cap-Bypass „Selber Arbeitgeber":
    //   • `cap_bypass`             — User-gesetzter Flag (Checkbox).
    //     Wird über Backend-Roundtrips preserved (Backend echoed in
    //     `_coerce_items`).
    //   • `_cap_bypassed`          — Server-bestätigter Marker, dass das
    //     Item tatsächlich vom Cap-Walk übersprungen wurde (= anrechenbar
    //     + cap_bypass + KV hat Bypass-Regel).
    //   • `_bypass_chain_warning`  — Soft-Warning-Text vom Backend, wenn
    //     die zeitliche Lücke zur nächsten Bypass-Position bzw. zum Stichtag
    //     größer als `max_gap_months` (= 24) ist. Bypass bleibt aktiv,
    //     Sachbearbeiter entscheidet.
    //   • `_bypass_gap_to_next_months` — gemessene Lücke (in M.) für Debug
    //     / Tooltip.
    cap_bypass: Boolean(it.cap_bypass) || Boolean(fallback.cap_bypass),
    // Iter 20.36.f2 — WT-Kanzlei-Kombiregel-Flag pro Zeile. Rein
    // Frontend-getriggert (Checkbox „In WT-Kanzlei erbracht"). Backend
    // reicht den Wert unverändert durch (`_coerce_items`); der Fallback
    // aus dem vorigen State schützt gegen Roundtrip-Verluste, falls
    // das Backend den Flag nicht auf jedem Endpoint echoed.
    wt_kanzlei: Boolean(it.wt_kanzlei) || Boolean(fallback.wt_kanzlei),
    // Iter 20.36.f5 — WT-Kanzlei-Kombiregel-Faktor-Reduktions-Marker
    // durchreichen. Frontend markiert reduzierte Non-WT-Zeilen; Backend
    // ist idempotent, aber die Marker helfen der UI zu erkennen, warum
    // eine Zeile mit Faktor 0.5 dasteht.
    _wt_non_kanzlei_reduced:
      Boolean(it._wt_non_kanzlei_reduced) ||
      Boolean(fallback._wt_non_kanzlei_reduced),
    _wt_previous_factor:
      typeof it._wt_previous_factor === "number"
        ? it._wt_previous_factor
        : typeof fallback._wt_previous_factor === "number"
          ? fallback._wt_previous_factor
          : null,
    // Iter 20.36.f3 — Altersfaktor-Regel-Marker durchreichen. Wird vom
    // Backend gesetzt, wenn die Regel einen Faktor reduziert hat. Frontend
    // nutzt den Marker für Provenance-Badge und Row-Highlight. Beim
    // nächsten Recalc-Roundtrip trägt das Frontend die Marker zurück ans
    // Backend, damit `_apply_facheinschlaegig_altersfaktor` idempotent
    // arbeiten kann.
    //
    // Iter 20.36.f14.6 — Fallback-Fix: Für Applier-Marker MUSS die Backend-
    // Antwort autoritär sein. Das alte `Boolean(it) || Boolean(fallback)`
    // hat einen `false`/`null`-Wert vom Backend durch einen alten `true`-
    // State aus dem Fallback überschrieben.
    //
    // Iter 20.36.f14.10 — Zusätzlich: Der Backend `pop()`t die Marker im
    // Revert (`_revert_altersfaktor`), sodass die Keys im JSON-Response
    // gar nicht mehr enthalten sind. Der `in`-Check würde false zurück-
    // liefern → Fallback würde greifen → alter Split bliebe stehen.
    // Deshalb komplett OHNE Fallback: Wenn Backend das Feld nicht liefert,
    // ist die Regel nicht (mehr) appliziert. Frontend zeigt keinen Split.
    _altersfaktor_applied: Boolean(it._altersfaktor_applied),
    _altersfaktor_previous_factor:
      typeof it._altersfaktor_previous_factor === "number"
        ? it._altersfaktor_previous_factor
        : null,
    // Iter 20.36.f14 — Altersfaktor-Split-Meta durchreichen. Enthält
    // alt/neu-Sub-Anteile (Datum-Ränder, Dauer-YMD, Faktoren). Frontend
    // rendert daraus zwei virtuelle Sub-Zeilen unter der Original-Zeile
    // (siehe `VordienstRow.jsx` Detail-Drawer).
    // Iter 20.36.f14.10 — Kein Fallback: Backend-`null`/fehlender Key
    // bedeutet, dass die Regel aktuell nicht (mehr) greift. Falls das
    // Frontend einen stale Split aus der Fallback-Kette rein-injizieren
    // würde, entstünde eine visuelle Inkonsistenz nach Nutzer-Toggles
    // (z.B. Aktivieren weiterer Zeilen triggert die 5-J-Ausnahme →
    // Backend cleaned den Split → Fallback dürfte ihn nicht reanimieren).
    _altersfaktor_split:
      it._altersfaktor_split && typeof it._altersfaktor_split === "object"
        ? it._altersfaktor_split
        : null,
    // Iter 20.36.f14.12 — Präsenzdienst-Regel-Marker durchreichen.
    // Backend `_apply_praesenzdienst_regel` setzt sie beim Applier-Run;
    // Frontend `VordienstRow.jsx` rendert daraus:
    //   • gelben Warn-Balken „Umschließung durch DV prüfen" (modus="gesetzlich")
    //   • kompakten Warn-Icon-Chip im kollabierten Row-Header (Iter 20.36.f14.14)
    // Kein Fallback: Backend ist autoritär. Wenn Regel deaktiviert wird,
    // clean der Applier die Marker aus dem Response-Dict.
    _praesenzdienst_regel_applied: Boolean(it._praesenzdienst_regel_applied),
    _praesenzdienst_needs_review: Boolean(it._praesenzdienst_needs_review),
    // Iter 20.36.f14.12 — User-Manual-Override-Marker: bleibt persistent,
    // damit der Backend-Applier eine bewusst deaktivierte Zeile nicht in
    // Folge-Recalcs wieder auf anrechenbar=True setzt. Fallback aus
    // vorigem State, weil das Feld ein reines Roundtrip-Meta ist.
    _praesenzdienst_manually_disabled:
      Boolean(it._praesenzdienst_manually_disabled)
      || Boolean(fallback._praesenzdienst_manually_disabled),
    // Iter 20.36.f14.22 — Frontend-only „Geprüft"-Toggle. Wird per Klick
    // auf das Info-Badge in `VordienstRow.jsx` gesetzt. Backend kennt das
    // Feld nicht (ignoriert es im Recalc-Request). Fallback aus vorigem
    // State preserved den Zustand über Recalc-Roundtrips — bei einem
    // Re-Extract (kein Fallback beim Extract-Path in StepThree.jsx:441)
    // startet das Feld korrekt bei `false`.
    _review_verified:
      Boolean(it._review_verified)
      || Boolean(fallback._review_verified),
    _cap_bypassed: Boolean(it._cap_bypassed),
    _bypass_chain_warning:
      typeof it._bypass_chain_warning === "string" && it._bypass_chain_warning.length > 0
        ? it._bypass_chain_warning
        : null,
    _bypass_gap_to_next_months:
      typeof it._bypass_gap_to_next_months === "number"
        ? it._bypass_gap_to_next_months
        : null,
    // Iter 19.63.104 — KV-Regel-Truth-Value vom Backend durchreichen.
    // `_coerce_items` setzt das auf True, wenn `match_kv_factor` einen
    // direkten Stichwort-Treffer auf einem voll/hälftig/nicht-Bucket hatte.
    // `classifyFactorProvenance` nutzt das als HÖCHSTE Priorität — kein
    // heuristisches Raten aus Begründungs-Phrasen mehr.
    _kv_rule_applied:
      Boolean(it._kv_rule_applied) || Boolean(fallback._kv_rule_applied),
    // Iter 19.64.10 — Provenienz-Flag „manuell hinzugefügt": vom
    // Sachbearbeiter selbst angelegte Zeilen (via „+ Zeile hinzufügen")
    // sollen NICHT als LLM-Annahme klassifiziert werden. Wird beim Backend-
    // Roundtrip preserved, falls Backend den Flag echoed (oder Fallback
    // liefert ihn aus dem vorigen Frontend-State).
    _user_added:
      Boolean(it._user_added) || Boolean(fallback._user_added),
    // Iter 19.66.5 — Manuell-Faktor-Sperre: Frontend-only Flag, das den
    // automatischen KV-Regel-Override bei Tätigkeits-Änderungen blockiert
    // und das Provenienz-Badge auf „Manuell" pinnt. Backend kennt das Feld
    // nicht; daher MUSS der vorige Frontend-State (fallback) den Flag
    // beim Roundtrip wieder einspeisen — sonst geht die Sperre nach jedem
    // „Summe aktualisieren" verloren.
    _factor_manual:
      Boolean(it._factor_manual) || Boolean(fallback._factor_manual),
    // Iter 19.68 — Extraction-Quality-Schicht: Soft-Warning- und Auto-
    // Korrektur-Audit-Arrays vom Backend durchreichen. `QualityBadges`
    // im VordienstRow.jsx rendert basierend darauf das „Bitte prüfen"-
    // sowie das „Auto-Korrektur"-Badge. Wenn Backend keinen Wert liefert
    // (z.B. beim Review-Roundtrip „Summe aktualisieren", der keine
    // Quality-Pipeline durchläuft), Fallback aus dem vorigen Frontend-
    // State übernehmen — sonst verschwinden die Badges beim ersten Recalc.
    _quality_flags: Array.isArray(it._quality_flags)
      ? it._quality_flags
      : (Array.isArray(fallback._quality_flags) ? fallback._quality_flags : undefined),
    _auto_corrected: Array.isArray(it._auto_corrected)
      ? it._auto_corrected
      : (Array.isArray(fallback._auto_corrected) ? fallback._auto_corrected : undefined),
    // Iter 19.68.3 — User-Acknowledgement der Quality-Warning. Wird gesetzt:
    //   • Implizit: bei jeder manuellen Bearbeitung der Row (Datum/AG/
    //     Tätigkeit/Faktor/ANR/etc.) im VordienstRow `onChange`-Wrapper.
    //   • Explizit: via „Position geprüft & bestätigt"-Checkbox in den
    //     QualityBadges-Detailbox.
    // Frontend-only Flag (Backend kennt es nicht für Math), aber wird
    // beim Final-Recommendation-Request mitgeschickt, damit das PDF
    // sehen kann, welche Warnungen der User explizit ignoriert hat.
    _quality_ack:
      Boolean(it._quality_ack) || Boolean(fallback._quality_ack),
    // Iter 19.68.6 — Konsolidierungs-Lücken-Tage durchschleifen, damit
    // `computeRowDauer` (Frontend) und `_compute_row_dauer` (Backend)
    // dieselbe Lücken-Subtraktion anwenden. Ohne diesen Flag würde das
    // Frontend nach einem Review-Roundtrip die Brutto-Range-Tage zählen
    // und 4 T. statt korrekter 3 T. anzeigen.
    _consolidation_gap_days:
      typeof it._consolidation_gap_days === "number"
        ? it._consolidation_gap_days
        : (typeof fallback._consolidation_gap_days === "number"
            ? fallback._consolidation_gap_days
            : 0),
    // Iter 20.03 (Bug: Martina Fall 2, 3. Iteration) — Backend-Provenienz-
    // Marker durchschleifen. Ohne diese Felder sieht das Frontend immer
    // `_source_kind=""` (= CV-uncertain) und rendert fälschlich ANNAHME,
    // auch wenn das Backend das Dokument korrekt als SV-Auszug klassifiziert
    // hat. Fallback aus dem vorigen State, weil `recalc_vordienstzeiten`
    // diese Backend-internen Marker nicht immer neu setzt.
    _source_kind:
      typeof it._source_kind === "string" && it._source_kind
        ? it._source_kind
        : (typeof fallback._source_kind === "string" ? fallback._source_kind : ""),
    _kv_match_type:
      typeof it._kv_match_type === "string" && it._kv_match_type
        ? it._kv_match_type
        : (typeof fallback._kv_match_type === "string" ? fallback._kv_match_type : ""),
    _auto_fix_iter102:
      typeof it._auto_fix_iter102 === "boolean"
        ? it._auto_fix_iter102
        : Boolean(fallback._auto_fix_iter102),
    _auto_fix_iter1974_interpretation:
      typeof it._auto_fix_iter1974_interpretation === "boolean"
        ? it._auto_fix_iter1974_interpretation
        : Boolean(fallback._auto_fix_iter1974_interpretation),
    _auto_fix_iter1975_annahme_prefix:
      typeof it._auto_fix_iter1975_annahme_prefix === "boolean"
        ? it._auto_fix_iter1975_annahme_prefix
        : Boolean(fallback._auto_fix_iter1975_annahme_prefix),
  };
}

/** Iter 19.63.28 — `aggregateHybridYMD` und Helper `_formatYMD` wurden
 *  ENTFERNT. Das Backend ist jetzt Single Source of Truth für alle
 *  Aggregat-Berechnungen (siehe `_aggregate_hybrid_ymd` in
 *  `vordienstzeiten.py`). Das Frontend nutzt `backendAggregate.display`
 *  + `backendAggregate.roh.display` direkt — keine eigene FE-Math mehr.
 *
 *  Falls jemals ein FE-Fallback nötig wird (z.B. für Edit-Time-
 *  Plausibilitätschecks ohne Backend-Roundtrip), nutze die
 *  per-Item-Funktionen `monthsBetween`, `diffYMD`, `effectiveDurationYMD`
 *  weiter oben in diesem File.
 */

/**
 * Format a duration given as fractional months into a compact German
 * "X J. Y M. Z T." label. Examples:
 *   12.0    → "1 J."
 *   13.0    → "1 J. 1 M."
 *   23.6    → "1 J. 11 M. 18 T."
 *   84.0    → "7 J."
 *   95.5    → "7 J. 11 M. 15 T."
 *   0.329   → "10 T."
 *   0       → "0 T."
 *
 * Pure arithmetic decomposition — no calendar anchor / no Math.round on
 * total days (which previously caused 12 mo × 30.4375 = 365.25 → 365 →
 * "0 J. 11 M. 30 T."). Integer-near month values stay exact.
 *
 *   integer-near months (±0.034 ≈ ±1 day) → clean Y/M, no days
 *   fractional months                     → (frac × 30.4375) rounded to days
 */
/** Iter 19.63.32 — `formatDuration` (months-input → display-string) wurde
 *  entfernt. Es war nur noch ein Alias-Wrapper für die alten Code-Pfade.
 *  Alle aktuellen Aufrufer nutzen:
 *    - Backend-`display` aus `backendAggregate.display` (zentral)
 *    - `formatDurationDays(days)` für direkte Tage-Anzeige (legacy preview).
 */
export function sumAnrechenbareMonate(items, stichtag) {
  return items.reduce((sum, it) => {
    if (!it.anrechenbar) return sum;
    // Prefer the backend-computed effective months (already corrected for
    // overlap-resolution and cap). Fall back to raw months × faktor for
    // manually-added rows that haven't been processed yet.
    if (typeof it.months_anrechenbar === "number") {
      return sum + it.months_anrechenbar;
    }
    // Iter 19.63.73 — Fallback nutzt strikt 30/360-Math (matcht Backend),
    // damit die Live-Vorschau nach Faktor-Edits keinen Calendar-Drift
    // gegen `_aggregate_hybrid_ymd` produziert.
    const m = monthsBetween30360(it.datum_von, it.datum_bis, stichtag);
    const f = typeof it.anrechnungsfaktor === "number" ? it.anrechnungsfaktor : 1.0;
    return sum + m * f;
  }, 0);
}

/**
 * Iter 19.63.80 — Faktor-Provenienz-Klassifikation.
 *
 * Pro Vordienst-Zeile klassifizieren wir, WOHER der aktuelle Anrechnungs-
 * Faktor stammt. Das Ergebnis steuert ein Pill-Badge in der Faktor-Spalte
 * und gibt dem Sachbearbeiter sofort einen Sicherheits-Indikator:
 *
 *   "kv-rule"      → KV-Faktor-Regel (`faktor_regeln.{voll,haelftig,nicht}`)
 *                    matcht ein Stichwort in der Tätigkeit. Sicher.
 *   "kv-praktikum" → User hat im Praktikum-Inline-Selector gewählt UND
 *                    die KV-Praktikum-Regel (`praktikum_regeln`) oder die
 *                    Fallback-Regel hat den Faktor gesetzt. Sicher.
 *   "manual"       → Faktor wurde manuell verändert (z.B. via Begründungs-
 *                    Edit) und weicht vom Default 1.0 ab — KEIN KV-Match.
 *                    Sichtbar als „Override" für Audit.
 *   "default"      → Default-Faktor 1.0 wurde herangezogen, weil weder
 *                    eine KV-Regel matched noch ein Praktikum oder
 *                    manuelle Wahl vorliegt. User sollte prüfen ob z.B.
 *                    Anstellungstyp (Angestellter/Arbeiter) eindeutig war.
 *   "n/a"          → Nicht anrechenbar (anrechenbar=false) — Faktor egal.
 *
 * Reine FE-Heuristik, keine Backend-Änderung. Greift deterministisch auf
 * vorhandene Row-Felder + KV-Regel-Dict zurück.
 */
export function classifyFactorProvenance(row, faktorRegeln, opts = {}) {
  if (!row || row.anrechenbar === false) return "n/a";

  // Iter 20.03.P5-fix (Punkt aus Martina-Fall-4-Report Runde 2) —
  // Praktikum-Anstellungstyp-Wahl ist DETERMINISTISCH.
  // Sobald der User im Inline-Selector Angestellter/Arbeiter gewählt
  // hat, greift die KV-Praktikum-Regel bzw. der KV-Faktor-Rule-Match
  // via Tätigkeits-Prefix ("Angestellter — …"). Diese Wahl überschreibt
  // die CV-Uncertain-Regel (Iter 19.74 Baustein C) UND den Backend-
  // `_auto_fix_iter102`-Downgrade, weil der User explizit die
  // Klassifikation vorgenommen hat. Ohne dieses Vorziehen würde eine
  // Praktikum-Zeile aus einem Lebenslauf beim KV-Gewerbe (mit
  // `equivalence_check_required=true`) sofort wieder auf ANNAHME
  // zurückspringen — was den Auto-Close-Fix von Punkt 3 der Runde 1
  // sinnlos machen würde. Die Timeline zeigt es bereits als KV-Regel.
  if (
    row.praktikum_anstellungstyp === "angestellter" ||
    row.praktikum_anstellungstyp === "arbeiter"
  ) {
    return "kv-praktikum";
  }

  // Iter 19.74 (Baustein C) — Wenn die Zeile aus einem Lebenslauf stammt und
  // der KV eine Gleichwertigkeitsprüfung verlangt, ist die Klassifikation
  // NIE deterministisch (auch nicht bei KV-Regel-Match). Der Sachbearbeiter
  // muss die Gleichwertigkeit prüfen — daher zeigen wir ANNAHME statt
  // KV-REGEL. Regel greift auch, wenn `_source_kind` unbekannt ist (leer),
  // damit historische Items konservativ als CV behandelt werden.
  //
  // Iter 19.75 — AUSNAHME: Bei deterministischen SV-Statuswörtern
  // (Zivildienst, Präsenzdienst, Karenz, AMS-Bezug etc.) ODER bei
  // wörtlichen KV-Kategorie-Matches (`_kv_match_type in {direct,partial}`)
  // ist die Klassifikation auch aus einem CV eindeutig — keine ANNAHME.
  const _cvUncertain =
    ["lebenslauf", "mixed", "other", ""].indexOf(
      String(row._source_kind || "").toLowerCase(),
    ) !== -1;
  const _mt = String(row._kv_match_type || "").toLowerCase();
  const _isDeterministicMatch =
    _mt === "direct" || _mt === "partial"
    || isDeterministicSvStatus(
      row.taetigkeit || "",
      row.anrechenbarkeit_begruendung || "",
    );
  if (
    opts.equivalenceCheckRequired === true
    && _cvUncertain
    && row._auto_fix_iter102 !== true  // nicht doppelt behandeln
    && !_isDeterministicMatch
  ) {
    // ANNAHME wird weiter unten via `_auto_fix_iter102`-Check ODER via
    // Fallthrough auf "default" erreicht — wir setzen hier bereits das
    // deterministische Signal, damit KEIN weiterer heuristischer Match
    // die Zeile wieder auf „kv-rule" hochdreht.
    return "default";
  }

  // Iter 19.63.80 — Bei einem noch unentschiedenen Praktikum übernimmt
  // der amber PRAKTIKUM-Badge + der Banner mit Sprung-Links die User-
  // Lenkung. Ein zusätzliches „Default"-Faktor-Badge wäre redundant
  // und visuell verwirrend (zwei amber/slate-Badges nebeneinander).
  // → kein Provenienz-Badge anzeigen, bis Typ gewählt wurde.
  if (isPraktikumPosition(row.taetigkeit) && !row.praktikum_anstellungstyp) {
    return "n/a";
  }

  // Iter 19.66.5 — HÖCHSTE Priorität: Explizite manuelle Faktor-Sperre.
  // Wenn der User den Anrechnungsfaktor über das %-Eingabefeld manuell
  // verändert hat, ist das IMMER „Manuell" — unabhängig vom gewählten
  // Wert (auch wenn er zufällig wieder bei 100 % landet) und unabhängig
  // von ambivalenten Tätigkeitstexten, LLM-Annahme-Signalen oder KV-
  // Rule-Truth-Values. Der User hat explizit die Kontrolle übernommen.
  if (row._factor_manual === true) return "manual";

  // Iter 19.65.21 — DEFINITIVES Backend-Signal: Wenn `_coerce_items` das
  // Item explizit zum ANNAHME gedowngraded hat (`_auto_fix_iter102=true`),
  // ist KEINE Frontend-Heuristik mehr erlaubt, die das zurück zu „kv-rule"
  // dreht. Damit greift der Backend-Override gegen LLM-Sprach-Variationen
  // KONSISTENT bis ins UI.
  if (row._auto_fix_iter102 === true) return "default";

  // Iter 19.65.21 — Ambivalente Tätigkeits-Heuristik HAT VORRANG vor
  // `_kv_rule_applied`-Truth-Value, weil:
  //  (a) Stale-State-Schutz: gecachte Items aus pre-Iter-19.65.21-DB
  //      könnten noch `_kv_rule_applied=true` haben, obwohl die Tätigkeit
  //      anstellungstyp-ambivalent ist (Reinigungskraft etc.).
  //  (b) Spiegelt das Backend `_is_anstellungstyp_ambivalent_taetigkeit`-
  //      Verhalten, damit Frontend + Backend KONSISTENT klassifizieren —
  //      auch wenn das Backend (z.B. wegen Async-Race oder DB-Cache) noch
  //      einen alten Truth-Value zurückgibt.
  //  (c) Notwendig für User-added-Zeilen (Frontend-EmptyRow), die nicht
  //      durch `_coerce_items` laufen.
  if (isAnstellungstypAmbivalentTaetigkeit(row.taetigkeit || "")) {
    return "default";
  }

  // Iter 19.63.104 — Backend-Truth-Value: Wenn `_coerce_items` einen
  // direkten KV-Faktor-Regel-Treffer hatte, ist die Klassifikation
  // deterministisch — KEIN heuristisches Raten mehr nötig.
  if (row._kv_rule_applied === true) return "kv-rule";

  // Iter 19.63.90 — ANNAHME-Signal-Override (HÖCHSTE Priorität unter den
  // Wenn die LLM-Begründung explizit signalisiert, dass eine Annahme
  // getroffen wurde („zu 100 % angenommen", „Typ in der Quelle nicht
  // eindeutig"), darf KEIN nachfolgender KV-Stichwort-Match dieses
  // Signal überschreiben. Hintergrund (Bug-Report Martina-Fall-4):
  // Die durch Iter 81-86 in der Begründung erzwungene defensive Boiler-
  // plate „Bitte vom Sachbearbeiter prüfen" matchte versehentlich das
  // KV-Stichwort „Sachbearbeiter" (voll-Bucket vieler KVs) im Iter-87-
  // Begründungs-Fallback → fälschlich „KV-Regel"-Badge statt ANNAHME.
  // Defense: das ANNAHME-Signal des LLM hat IMMER Vorrang.
  if (hasAnnahmeSignal(row.anrechenbarkeit_begruendung || "")) {
    return "default";
  }

  // Iter 19.66.5 — Manuelle Faktor-Sperre wird bereits ganz oben behandelt
  // (vor allen Heuristik-Defenses). Hier nichts mehr nötig.

  // 2. KV-Faktor-Regel matcht ein Stichwort in der Tätigkeit?
  //    Iter 19.63.90 — Faktor-Konsistenz-Check: der gefundene Regel-
  //    Faktor MUSS mit dem tatsächlichen row.anrechnungsfaktor über-
  //    einstimmen. Sonst beschreibt die Regel den aktuellen Faktor nicht
  //    (z.B. „geringfügig" → nicht-Bucket faktor=0, aber row.faktor=1.0
  //    = LLM-Annahme entgegen der KV-Regel). Mismatch → kein kv-rule.
  const rule = applyKvFactorRule(row.taetigkeit || "", faktorRegeln || {});
  if (rule && _ruleFaktorMatchesRow(rule, row)) return "kv-rule";

  // 2b. Iter 19.63.87 — Begründungs-Fallback: Tätigkeits-Match scheiterte,
  //     aber die LLM-Begründung enthält oft den eigentlichen KV-Bezug
  //     (typisch bei SV-Auszug-Items wie „Zivildienst", „§2Abs.1Z4 GSVG").
  //     Wir matchen sekundär in der Begründung; Negation-Check verhindert
  //     False-Positives wie „… wäre wie Arbeiter hälftig".
  //     Iter 19.63.90 — gleiche Faktor-Konsistenz-Check (siehe 2.).
  const ruleFromBegr = applyKvFactorRuleFromBegruendung(
    row.anrechenbarkeit_begruendung || "",
    faktorRegeln || {},
  );
  if (ruleFromBegr && _ruleFaktorMatchesRow(ruleFromBegr, row)) return "kv-rule";

  // 2c. Iter 19.63.89 — SV-Code-basierte Provenienz: Wenn die Tätigkeit
  //     oder Begründung typische SV-Auszug-Paragraphen-Verweise enthält
  //     (§2 GSVG, §3 BSVG, §2 FSVG, §19a ASVG mit Beschäftigungs-Zusatz,
  //     „Selbstvers.") und das Backend per Iter-88-Disambiguierung
  //     anrechenbar=true gesetzt hat, ist die Klassifikation
  //     deterministisch via KV-Regel erfolgt — KEIN ANNAHME-Status nötig.
  if (isSvCodeBased(row)) return "kv-rule";

  // 3. Iter 19.66.8 — `f !== 1.0`-Pfad ENTFERNT. Vorher: jede Abweichung
  //    vom Default-Faktor 1.0 führte zu Badge „Manuell" — auch wenn der
  //    Wert vom LLM kam und keine andere Klassifikation greifen wollte
  //    (z.B. Tätigkeit-Edit ohne KV-Stichwort-Match). User-Wunsch:
  //    Badge „Manuell" NUR bei explizitem Faktor-Override via %-Input
  //    (`_factor_manual=true`, Top-Priority oben) oder bei manuell hin-
  //    zugefügten Zeilen (`_user_added=true`, nächster Schritt). LLM-
  //    Faktoren ohne Klassifikation fallen auf „Annahme" (default).

  // 4. Iter 19.64.10 — Manuell hinzugefügte Zeile (Default-Flag aus
  //    `EmptyRow()`). Wenn der Sachbearbeiter selbst eine Zeile angelegt
  //    hat und KEIN KV-Match greift (alle Pfade 1-3 sind durchgefallen),
  //    ist es definitiv keine LLM-Annahme — sondern eine vom Sachbearbeiter
  //    erfasste Position. → „Manuell" statt „Annahme".
  if (row._user_added === true) return "manual";

  // 5. Sonst: Default-Faktor 1.0 ohne KV-Stütze (typisch LLM-extrahierte
  //    Zeile mit nicht-eindeutiger Tätigkeit, kein ANNAHME-Signal in der
  //    Begründung). → „Annahme".
  return "default";
}

/**
 * Iter 19.63.90 — Helper: Stimmt der von einer KV-Regel zurückgegebene
 * Faktor mit dem tatsächlich gesetzten Row-Faktor überein (±0,001)?
 *
 * Wenn nicht, beschreibt die Regel das aktuelle Verhalten nicht — z.B.
 * matched die Regel das „nicht"-Bucket-Stichwort „geringfügig" (Faktor 0,
 * anrechenbar=false), während die Zeile real anrechenbar=true mit
 * Faktor 1,0 ist (LLM-Annahme entgegen der Regel). In dem Fall darf
 * KEIN „KV-Regel"-Badge gesetzt werden — der echte Zustand der Zeile
 * ist eine Annahme.
 */
function _ruleFaktorMatchesRow(rule, row) {
  if (!rule) return false;
  const rowF =
    typeof row?.anrechnungsfaktor === "number" ? row.anrechnungsfaktor : 1.0;
  return Math.abs(Number(rule.faktor) - rowF) < 0.001;
}

/**
 * Iter 19.63.90 — Erkennt das explizite ANNAHME-Signal aus den Backend-
 * Safety-Prompts (`_VORDIENST_SAFETY_RULES` in vordienstzeiten.py).
 *
 * Wenn die Quelle keinen eindeutigen Anstellungs-Typ-Beleg liefert,
 * zwingt der Prompt das LLM, eine deterministische Annahme zu treffen
 * (Faktor=1.0, anrechenbar=true) UND in der Begründung die exakte
 * Formulierung „… zu 100 % angenommen. Bitte vom Sachbearbeiter prüfen."
 * oder „Typ in der Quelle nicht eindeutig genannt …" zu verwenden.
 *
 * Diese Phrasen sind das verbindliche Sicherheits-Signal des LLM. Das
 * Frontend muss sie als Provenienz-„default" (ANNAHME-Badge) anerkennen
 * und darf NIE durch Stichwort-Heuristik (z.B. KV-Regel-Match auf
 * „Sachbearbeiter") überschreiben.
 *
 * Pattern-Design: tolerant gegenüber Whitespace-Varianten, aber strikt
 * genug, dass normale Begründungstexte (z.B. „voll anrechenbar laut KV")
 * KEIN Signal triggern.
 */
const _ANNAHME_SIGNAL_PATTERNS = [
  // „zu 100 % angenommen" / „zu 100% angenommen" / „100 %  angenommen"
  /zu\s*100\s*%\s*angenommen/i,
  // „Typ in der Quelle nicht eindeutig" / „Anstellungs-Typ … nicht eindeutig"
  /(?:typ|anstellungs[\s\-]?typ)[^.\n]{0,40}nicht\s+eindeutig/i,
  // Kombination „angenommen … Sachbearbeiter prüfen" (Backend-Boilerplate)
  /angenommen[\s\S]{0,60}sachbearbeiter\s+pr[üu]fen/i,
  // Iter 19.65.20 — Layer-C-Prompt erzwingt expliziten „Annahme:"-Prefix
  // bei single-Anstellungstyp-KVs ohne Dokumenten-Beleg. Frontend muss
  // diesen Marker erkennen, damit auch ohne Backend-Override (z.B. bei
  // gecachten Items oder LLM-Roundtrips) das ANNAHME-Badge erscheint.
  /^\s*annahme\s*[:—-]/im,
  /\bannahme\s*[:—-]\s*\w/i,
  // „nicht aus (dem) (Quell-)Dokument belegt"
  /nicht\s+(aus|im)\s+(dem\s+|einem\s+)?(quell)?dokument\s+belegt/i,
  // „Anstellungstyp nicht aus (dem) Dokument..."
  /anstellungstyp\s+nicht\s+aus\s+(dem\s+)?(quell)?dokument/i,
  // User-Repro-Phrasings aus Iter 19.65.19:
  /ohne\s+angabe\s+des\s+anstellungs/i,
  /ohne\s+(eindeutigen|klaren|expliziten)\s+beleg/i,
  /keine\s+angabe\s+des\s+anstellungs/i,
  // Iter 20.36.f14.13 — LLM-Sprach-Backup-Signal für den Rappitsch-CV-Fall.
  // Gemini schreibt manchmal „(aus Lebenslauf abgeleitet)" statt der
  // expliziten „Annahme:"-Boilerplate. Wenn der Text explizit sagt, dass
  // die Klassifikation aus dem Lebenslauf abgeleitet wurde, ist das
  // semantisch eine Annahme — der Sachbearbeiter muss die Gleichwertig-
  // keit prüfen. Zusätzliches Safety-Net zum Backend-Iter-20.36.f14.13-
  // Bidirektionalreklassifikator, falls dieser wg. LLM-Kommentar-Varianz
  // nicht greift.
  /aus\s+(dem\s+)?lebenslauf(?:-\s*kontext)?\s+(?:abgeleitet|hergeleitet|geschlossen|erschlossen)/i,
];

// ── Iter 20.36.f9 — Belegt-Contra-Signale (haben Vorrang vor Annahme-Match) ──
// Wenn eine Zeile eindeutige Beleg-Nachweise enthält (SV-Auszug, SV-Code,
// explizite Angestellte/Arbeiter-Aussage), gilt sie NIE als ANNAHME —
// selbst wenn das LLM zusätzlich noch das Wort „Annahme:" reingeschrieben
// hat (was der Prompt jetzt verbietet, aber der Frontend-Guard fängt Rest-
// widersprüche defensiv ab).
const _BELEGT_CONTRA_PATTERNS = [
  /\banstellungstyp\s+im\s+quelldokument\s+belegt\b/i,
  /\bexplizit\s+als\s+(angestellte|arbeiter)/i,
  /\bsv[-\s]?code\s+[a-z0-9§]+/i,
  /\baus\s+sv[-\s]?auszug\b/i,
  /\bsv[-\s]?auszug[-\s]?spalte\b/i,
  /\bim\s+sv[-\s]?auszug\s+als\s+(angestellte|arbeiter)/i,
  /\beindeutig\s+belegt\b/i,
];

function hasBelegtSignal(begruendung) {
  if (!begruendung || typeof begruendung !== "string") return false;
  // Nur den ersten Chunk der Begründung analysieren, damit Backend-Anhänge
  // (nach dem ersten „|") nicht fälschlich als Beleg gewertet werden.
  const firstChunk = begruendung.split("|", 1)[0];
  return _BELEGT_CONTRA_PATTERNS.some((p) => p.test(firstChunk));
}

export function hasAnnahmeSignal(begruendung) {
  if (!begruendung || typeof begruendung !== "string") return false;
  // Iter 20.36.f9 — Belegt-Contra-Check: Wenn die Zeile explizit als
  // dokumentbelegt markiert ist, ignorieren wir jegliches Annahme-Signal.
  // Das LLM widersprach sich manchmal selbst (schrieb „belegt" UND
  // „(Annahme: …)" in dieselbe Begründung); Frontend würde sonst das
  // ANNAHME-Badge zeigen.
  if (hasBelegtSignal(begruendung)) return false;
  return _ANNAHME_SIGNAL_PATTERNS.some((p) => p.test(begruendung));
}

// ── Iter 19.65.21 — Frontend-Spiegel der Backend-Tätigkeits-Heuristik ────────
// Spiegelt `_is_anstellungstyp_ambivalent_taetigkeit` aus
// `/app/backend/vordienstzeiten.py`. Greift in `classifyFactorProvenance`
// vor dem heuristischen `applyKvFactorRule`-Pfad und verhindert, dass das
// Frontend eigenständig auf „kv-rule" springt, obwohl der Anstellungstyp
// in der Tätigkeit nicht eindeutig ist.
//
// MUSS synchron mit dem Backend bleiben — Pattern-Änderungen IMMER an beiden
// Stellen vornehmen!
const _ANSTELLUNGSTYP_AMBIVALENT_FE_RE = new RegExp(
  [
    // Reinigung / Putzhilfe
    "\\breinigung\\w*",
    "\\bputz(frau|mann|kraft|personal|hilfe?|arbeit)",
    "\\bcleaning\\s*(staff|aid|service|personnel)?\\b",
    // Kasse
    "\\bkassier(in|er|erin|kraft)?\\b",
    "\\bkassa(kraft|personal)?\\b",
    // Lager / Logistik
    "\\blager(hilfe|arbeit(er(in)?)?|ist(in)?|personal|kraft|mitarbeiter(in)?)\\b",
    "\\blogistik(helfer(in)?|kraft|personal)\\b",
    // Verkauf
    "\\bverk[äa]ufer(in)?\\b",
    "\\bverkaufs(mitarbeiter(in)?|kraft|personal|berater(in)?|hilfe)\\b",
    // Gastro
    "\\bservice(kraft|personal|mitarbeiter(in)?)\\b",
    "\\bbedienung\\b",
    "\\bkellner(in)?\\b",
    "\\bk[öo]ch(in)?\\b",
    "\\bk[üu]chen(hilfe|kraft|personal|mitarbeiter(in)?)\\b",
    // Pflege / Betreuung
    "\\bpflege(hilfe|kraft|helfer(in)?|personal|assistenz)\\b",
    "\\bbetreuer(in)?\\b",
    "\\bbetreuungs(kraft|personal)\\b",
    // Security
    "\\bsicherheits(mitarbeiter(in)?|kraft|personal|dienst)\\b",
    "\\bsecurity(\\s+(staff|guard|officer|personal))?\\b",
    // Sonstige Hilfsfunktionen
    "\\bhelfer(in)?\\b",
    "\\bhilfsarbeit(er(in)?|skraft)?\\b",
    "\\b(b[üu]ro|office)(\\s|-)?(hilfe|kraft|assistent(in)?)\\b",
    // Fahrer / Kurier
    "\\bfahrer(in)?\\b",
    "\\bkurier(\\s+dienst)?\\b",
  ]
    .map((p) => `(?:${p})`)
    .join("|"),
  "i",
);
const _ANSTELLUNGSTYP_EXPLIZIT_FE_RE = new RegExp(
  "\\b(arbeiter(in|innen|:in|:innen)?|angestellte(r|n|:r)?|lehrling(e|s)?|" +
    "praktikant(in)?|trainee|geringf[üu]gig|werkstudent(in)?|" +
    "freie(r)?\\s+dienstnehmer(in)?)\\b",
  "i",
);

export function isAnstellungstypAmbivalentTaetigkeit(taetigkeit) {
  if (!taetigkeit || typeof taetigkeit !== "string") return false;
  if (_ANSTELLUNGSTYP_EXPLIZIT_FE_RE.test(taetigkeit)) return false;
  return _ANSTELLUNGSTYP_AMBIVALENT_FE_RE.test(taetigkeit);
}

/**
 * Liefert einen kurzen deutschen Erklär-Tooltip für ein Provenienz-Label.
 * Wird vom `FaktorProvenienzBadge`-Komponent gelesen, um dem Sachbearbeiter
 * via Hover den Kontext zu geben.
 */
export function describeFactorProvenance(provenance, row, faktorRegeln) {
  if (provenance === "kv-rule") {
    const rule = applyKvFactorRule(row?.taetigkeit || "", faktorRegeln || {});
    if (rule && rule.matched) {
      const cat =
        rule.category === "voll"
          ? "Voll anrechenbar"
          : rule.category === "haelftig"
            ? "Hälftig anrechenbar"
            : "Nicht anrechenbar";
      return `Faktor ${rule.faktor} laut KV-Regel — Stichwort „${rule.matched}\u201C (${cat}).`;
    }
    // Iter 19.63.87 — Fallback: Tätigkeit selbst hatte kein Stichwort,
    // aber die Begründung hat eines (z.B. Zivildienst → Präsenzdienst).
    const ruleFromBegr = applyKvFactorRuleFromBegruendung(
      row?.anrechenbarkeit_begruendung || "",
      faktorRegeln || {},
    );
    if (ruleFromBegr && ruleFromBegr.matched) {
      const cat =
        ruleFromBegr.category === "voll"
          ? "Voll anrechenbar"
          : "Hälftig anrechenbar";
      return (
        `Faktor ${ruleFromBegr.faktor} laut KV-Regel — Stichwort „${ruleFromBegr.matched}\u201C ` +
        `(${cat}, aus Begründungstext erkannt).`
      );
    }
    // Iter 19.63.89 — SV-Code-Klassifikation als Fallback-Quelle.
    // Wird angezeigt, wenn die Provenienz durch SV-Auszug-Paragraphen-
    // Pattern bestimmt wurde (kein KV-Stichwort-Match nötig).
    // Iter 19.63.99 — Erweitert um KV-Anrechnungsregel-Zitate
    // („lit. a", „Vollversicherung", „spezifische Anrechnungsregel").
    if (isSvCodeBased(row)) {
      return (
        "Faktor laut KV-Regel — deterministische Klassifikation aus " +
        "SV-Auszug-Markern (GSVG / BSVG / FSVG / ASVG-Selbstvers., " +
        "Vollversicherung) oder KV-Anrechnungsregel-Zitat (lit. a, " +
        "spezifische Anrechnungsregel)."
      );
    }
    return "Faktor laut KV-Regel gesetzt.";
  }
  if (provenance === "kv-praktikum") {
    const typLabel =
      row?.praktikum_anstellungstyp === "arbeiter" ? "Arbeiter" : "Angestellter";
    const f = typeof row?.anrechnungsfaktor === "number" ? row.anrechnungsfaktor : 1.0;
    return `Faktor ${f} laut KV-Praktikum-Regel — Sachbearbeiter-Wahl: ${typLabel}.`;
  }
  if (provenance === "manual") {
    const f = typeof row?.anrechnungsfaktor === "number" ? row.anrechnungsfaktor : 1.0;
    // Iter 19.64.10 — zwei Untervarianten von „Manuell":
    //   (a) Faktor wurde gegenüber dem Default 1,0 überschrieben →
    //       der Sachbearbeiter hat einen Wert manuell gesetzt.
    //   (b) Zeile wurde komplett vom Sachbearbeiter angelegt (Flag
    //       `_user_added`) und kein KV-Stichwort matcht → Default-Faktor 1,0,
    //       aber bewusst vom Menschen, NICHT vom LLM angenommen.
    if (row?._user_added === true && f === 1.0) {
      return (
        "Vom Sachbearbeiter manuell hinzugefügte Zeile — Faktor 1,0 (voll " +
        "anrechenbar) ist Standardwert. Bitte prüfen und gegebenenfalls anpassen."
      );
    }
    return `Faktor ${f} manuell überschrieben — kein KV-Regel-Match.`;
  }
  if (provenance === "default") {
    // Iter 19.63.81 — Sprachlich: „wurde zu 100 % angenommen" (vorher
    // „Default-Faktor verwendet"). Der Sachbearbeiter soll auf einen
    // Blick sehen, dass hier eine Annahme getroffen wurde und keine
    // KV-Regel den Faktor bestätigt hat.
    //
    // Iter 19.63.83 — Ergänzt um die LLM-Begründung (falls vorhanden),
    // damit der Sachbearbeiter SOFORT auf Hover sieht, was das Modell
    // zu dieser Zeile gesagt hat — ohne erst den Begründungs-Toggle
    // öffnen zu müssen. Erhöht die Transparenz über LLM-Entscheidungen
    // bei kritischen Annahme-Cases.
    const baseText =
      "Anrechnungsfaktor wurde zu 100 % angenommen — kein eindeutiger " +
      "KV-Regel-Match. Bitte prüfen, ob die Tätigkeit als Angestellter " +
      "oder Arbeiter eingestuft werden soll (z.B. Tätigkeitstext ergänzen, " +
      "damit die KV-Regel greift).";
    const llmHint =
      row && typeof row.anrechenbarkeit_begruendung === "string"
        ? row.anrechenbarkeit_begruendung.trim()
        : "";
    if (llmHint) {
      return `${baseText}\n\nHinweis aus Auswertung: ${llmHint}`;
    }
    return baseText;
  }
  return "";
}

/** Sum of `months` over all anrechenbar Ausbildungs-rows. */
export function sumAusbildungMonate(rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.reduce((s, r) => {
    if (!r || !r.anrechenbar) return s;
    const m = Number(r.months) || 0;
    return s + Math.max(0, m);
  }, 0);
}

/**
 * Chronological cap helper — used live in the UI to show users the
 * Höchstanrechnungsdauer impact before they hit "Alle Zeiten KI-prüfen".
 *
 * Walks items sorted by `datum_von` (oldest first), summing only those
 * marked `anrechenbar`. `ausbildungRows` are added BEFORE Beschäftigung
 * (chronologically pre-employment), matching the backend cap ordering.
 * Cap-bypass rows are added ON TOP after the cap is applied to the rest.
 *
 * Returns:
 *   - totalUncapped: raw months sum (no cap, both pools)
 *   - totalCapped:   bounded by capMonths PLUS bypass-credits
 *   - exceeded:      true if pre-bypass total > capMonths
 */
export function cappedAnrechenbareMonate(items, stichtag, capMonths, ausbildungRows = []) {
  const employmentRaw = sumAnrechenbareMonate(items, stichtag);
  const inCapAusb = (ausbildungRows || [])
    .filter((r) => r?.anrechenbar && !r?.bypass_cap)
    .reduce((s, r) => s + (Number(r.months) || 0), 0);
  const onTopAusb = (ausbildungRows || [])
    .filter((r) => r?.anrechenbar && r?.bypass_cap)
    .reduce((s, r) => s + (Number(r.months) || 0), 0);
  const cappedPool = employmentRaw + inCapAusb;
  const totalUncapped = cappedPool + onTopAusb;
  if (!capMonths || capMonths <= 0) {
    return { totalUncapped, totalCapped: totalUncapped, exceeded: false };
  }
  const totalCapped = Math.min(cappedPool, capMonths) + onTopAusb;
  return {
    totalUncapped,
    totalCapped,
    exceeded: cappedPool > capMonths + 0.5,
  };
}

/** Iter 19.42 — Days-based variant of cappedAnrechenbareMonate.
 *
 *  Computes effective anrechenbare days using sumAnrechenbareDays for
 *  Beschäftigungen and `months × 30` for Ausbildungs-Credits.
 *
 *  Returns:
 *    - totalUncappedDays / totalCappedDays / exceeded (all days)
 */
export function cappedAnrechenbareDays(items, stichtag, capMonths, ausbildungRows = []) {
  const employmentDays = sumAnrechenbareDays(items, stichtag);
  const inCapAusbDays = (ausbildungRows || [])
    .filter((r) => r?.anrechenbar && !r?.bypass_cap)
    .reduce((s, r) => s + Math.max(0, (Number(r.months) || 0) * 30), 0);
  const onTopAusbDays = (ausbildungRows || [])
    .filter((r) => r?.anrechenbar && r?.bypass_cap)
    .reduce((s, r) => s + Math.max(0, (Number(r.months) || 0) * 30), 0);
  const cappedPool = employmentDays + inCapAusbDays;
  const totalUncappedDays = cappedPool + onTopAusbDays;
  const capDays = capMonths > 0 ? capMonths * 30 : 0;
  if (capDays <= 0) {
    return {
      totalUncappedDays,
      totalCappedDays: totalUncappedDays,
      exceeded: false,
    };
  }
  const totalCappedDays = Math.min(cappedPool, capDays) + onTopAusbDays;
  return {
    totalUncappedDays,
    totalCappedDays,
    exceeded: cappedPool > capDays + 15, // ~0.5 month tolerance
  };
}


/**
 * Apply the KV's structured factor rules to a single tätigkeit string.
 * Mirrors the backend's `match_kv_factor` so manual frontend edits get
 * the same deterministic factor without needing an API roundtrip.
 *
 * Returns { faktor, anrechenbar, begruendung, matched, category } or null.
 *
 * Severity order: nicht > haelftig > voll (defensive — exclusions are
 * usually the most precisely-worded entries in the KV).
 *
 * Iter 19.31.
 */
// ─── Audit-Fix M12 (Iter 19.70) — Compound-False-Positive-Guard ─────────────
// Spiegel von `_kw_occurs` im Backend (`vordienstzeiten.py`):
// „sachBEarbeiter"/„MITarbeiter" enthalten „arbeiter", sind aber keine
// Arbeiter („…bearbeiter" kommt von „bearbeiten"). Echte Komposita wie
// „Facharbeiter"/„Bauarbeiter" bleiben Matches. FE/BE MÜSSEN identisch
// matchen — Parity-Test: test_iter1970_audit_medium.py.
export function kwIndexOf(fragment, text) {
  if (!fragment) return -1;
  if (!fragment.startsWith("arbeit")) return text.indexOf(fragment);
  let start = 0;
  for (;;) {
    const i = text.indexOf(fragment, start);
    if (i < 0) return -1;
    const before2 = text.slice(Math.max(0, i - 2), i);
    const before3 = text.slice(Math.max(0, i - 3), i);
    if (before2 === "be" || before3 === "mit") {
      start = i + 1;
      continue;
    }
    return i;
  }
}

export function applyKvFactorRule(taetigkeit, faktorRegeln) {
  if (!taetigkeit || !faktorRegeln) return null;
  const t = String(taetigkeit).toLowerCase().replace(/\([^)]*\)/g, "").trim();
  if (!t) return null;
  const buckets = [
    ["nicht", 0.0, "Nicht"],
    ["haelftig", 0.5, "Hälftig"],
    ["voll", 1.0, "Voll"],
  ];
  for (const [cat, faktor, label] of buckets) {
    const keywords = faktorRegeln[cat] || [];
    for (const kw of keywords) {
      if (!kw || kw.length < 3) continue;
      let hit = null;
      if (kwIndexOf(kw, t) >= 0) hit = kw;
      else if (kw.length >= 5 && kwIndexOf(kw.slice(0, -1), t) >= 0) hit = kw;
      if (hit) {
        return {
          faktor,
          anrechenbar: faktor > 0,
          begruendung: `[Faktor ${faktor} gesetzt gemäß KV-Regel „${label} anrechenbar: ${hit}".]`,
          matched: hit,
          category: cat,
        };
      }
    }
  }
  // ── Iter 19.66.6 — KV-Implicit-Default-voll für explizite Anstellungstypen ──
  // Hintergrund: Manche KVs (z.B. KV Gewerbe) haben eine LEERE `voll`-Bucket
  // und listen nur die Ausnahmen in `nicht`/`haelftig` (typische öster-
  // reichische KV-Konvention: „Alle Vordienstzeiten sind voll anrechenbar,
  // AUSSER folgender Ausnahmen [Liste]"). Wenn der User explizit
  // „Angestellter" eintippt UND kein Ausschluss-Bucket matcht, ist die
  // implizite KV-Aussage: voll anrechenbar.
  //
  // Bedingungen:
  //   (a) Tätigkeit matcht den expliziten Anstellungstyp-RE
  //       (angestellter/arbeiter/lehrling/etc. — kein ambivalenter Begriff).
  //   (b) KV hat überhaupt Regel-Struktur (mind. ein Bucket ist befüllt) —
  //       sonst ist das KV ggf. fehlkonfiguriert und wir würden silent
  //       100 % anrechnen, obwohl der User es nicht erwartet.
  //
  // Liefert dasselbe Shape wie ein normales Bucket-Match mit category
  // `voll-default` + matched=`<KV-Default>`, damit das Frontend visuell
  // sehen kann, dass die Regel implizit (nicht über Stichwort) gegriffen
  // hat.
  const hasAnyBucket =
    (faktorRegeln.nicht && faktorRegeln.nicht.length > 0) ||
    (faktorRegeln.haelftig && faktorRegeln.haelftig.length > 0) ||
    (faktorRegeln.voll && faktorRegeln.voll.length > 0);
  if (hasAnyBucket && _ANSTELLUNGSTYP_EXPLIZIT_FE_RE.test(taetigkeit)) {
    return {
      faktor: 1.0,
      anrechenbar: true,
      begruendung:
        "[Faktor 1 gesetzt gemäß KV-Default: Anstellungstyp explizit, " +
        "keine Ausschluss-Regel des KV greift → voll anrechenbar.]",
      matched: "<KV-Default>",
      category: "voll-default",
    };
  }
  return null;
}

/**
 * Iter 19.63.87 — Begründungstext-basierter KV-Regel-Match-Fallback.
 *
 * Wird genutzt, wenn `applyKvFactorRule(taetigkeit)` kein Match liefert,
 * weil die Tätigkeitsbezeichnung selbst kein KV-Stichwort enthält
 * (typische SV-Auszug-Begriffe: „Zivildienst", „§2Abs.1Z4 GSVG",
 * „BMS-Bezug", „Karenzgeld"). In solchen Fällen erwähnt der LLM-Output
 * den eigentlichen KV-Bezug in der Begründung („dem Präsenzdienst
 * gleichgestellt", „gewerblich selbständig laut KV-Regelwerk").
 *
 * Strategie:
 *   • Match nur auf „voll" + „haelftig"-Buckets (NICHT „nicht" —
 *     anrechenbar=false-Items werden eh schon vorher gefiltert).
 *   • Pro Treffer: Anti-False-Positive-Negation-Check — wenn innerhalb
 *     der letzten 30 Zeichen vor dem Stichwort eine Negation steht
 *     („nicht", „kein", „wäre", „statt", „im Gegensatz", „anders als"),
 *     gilt der Treffer als nicht-aussagekräftig und wird verworfen.
 *
 * Liefert dasselbe Shape wie `applyKvFactorRule`, mit zusätzlichem
 * `source: "begruendung"`-Marker für Tooltip-Hinweise.
 */
const _NEG_WINDOW = 30;
const _NEG_HINTS = [
  "nicht",
  "kein",
  "keine",
  "keinen",
  "wäre",
  "waere",
  "statt",
  "anstatt",
  "im gegensatz",
  "anders als",
  "ohne",
];

/**
 * Iter 19.63.88 — Phrasen, die in einer Begründung eine GLOBALE Negation
 * signalisieren. Wird sowohl von `applyKvFactorRuleFromBegruendung` als
 * auch von `isSvCodeBased` (Iter 19.63.89) genutzt, um Backend-Selbst-
 * widersprüche (anrechenbar=true mit „nicht anrechenbar"-Begründung)
 * defensiv abzufangen.
 */
const _NEG_GLOBAL_PHRASES = [
  "nicht anrechenbar",
  "nicht angerechnet",
  "nicht angerechnet werden",
  "explizit nicht",
  "nicht anrechen", // „nicht anrechenfähig" etc.
  "keine anrechnung",
];

function _hasGlobalNegation(text) {
  const t = String(text || "").toLowerCase();
  if (!t) return false;
  return _NEG_GLOBAL_PHRASES.some((p) => t.includes(p));
}

/**
 * Iter 19.63.89 — SV-Code-basierte Provenienz-Erkennung.
 *
 * Tätigkeiten aus österreichischen SV-Auszügen tragen oft Paragraphen-
 * Verweise als „Tätigkeitsbezeichnung" (§2 GSVG, §3 BSVG, §2 FSVG, §19a
 * ASVG, „Selbstvers." etc.). Diese sind durch die Iter-19.63.88-Prompt-
 * Disambiguierung im Backend bereits deterministisch klassifiziert
 * (anrechenbar=true bei Selbständigkeit, anrechenbar=true bei §19a-
 * mit-Beschäftigungs-Zusatz). Sie sollen daher KEIN ANNAHME-Badge mehr
 * bekommen — die KV-Regel-Anwendung ist eindeutig.
 *
 * Diese Funktion liefert `true`, wenn:
 *   1. Tätigkeit ODER Begründung ein SV-Code-Pattern enthält
 *      (§+Zahl+ASVG/GSVG/BSVG/FSVG, „GSVG"/„BSVG"/„FSVG"/„ASVG" als
 *      eigenständiges Wort, oder „Selbstvers"); UND
 *   2. die Begründung KEINE globale Negation enthält (Defense-in-Depth
 *      gegen Backend-Selbstwidersprüche, siehe Iter 19.63.88).
 *
 * Genutzt in `classifyFactorProvenance` als zusätzlicher Pfad zu
 * `kv-rule`, damit der Sachbearbeiter bei SV-Code-Items sofort sieht:
 * „Das ist nicht ANNAHME, das ist KV-Regel-Klassifikation."
 */
const _SV_CODE_PATTERNS = [
  // §+Zahl mit optionalen Absatz-/Ziffer-Zusätzen + Gesetzes-Kürzel
  // (matched z.B. „§2Abs.1Z4 GSVG", „§ 19a ASVG", „§3 BSVG")
  /§\s*\d+\s*[a-z]?(\s*(abs|art)\.?\s*\d+)?(\s*z(iff)?\.?\s*\d+)?\s*[\.\s]*\s*(asvg|gsvg|bsvg|fsvg)/i,
  // Gesetzes-Kürzel als eigenständiges Wort (matched „§2 GSVG", aber
  // auch wenn der Paragraph fehlt: „GSVG ohne PV-Pflichtversicherung").
  /\b(gsvg|bsvg|fsvg)\b/i,
  // „Selbstvers." als typische SV-Auszug-Bezeichnung (z.B. §19a ASVG
  // mit Beschäftigungs-Zusatz).
  /selbstvers(icherung|\.)/i,
  // Iter 19.63.99 — Vollversicherungs-Klassifikation (BV0X-Familie). Typische
  // LLM-Phrase aus SV-Auszug-Items: „Arbeiterverhältnis (Vollversicherung)",
  // „Angestelltenverhältnis (Vollversicherung)". Negationen werden durch
  // _hasGlobalNegation in isSvCodeBased blockiert.
  /vollversicherung/i,
  // Iter 19.63.99 — Expliziter KV-Anrechnungsregel-Verweis. Wenn das LLM
  // „laut spezifischer Anrechnungsregel" schreibt, hat es eine konkrete
  // KV-Regel zitiert — keine 100%-Annahme mehr.
  /spezifische[rn]?\s+anrechnungsregel/i,
  // Iter 19.63.99 — Lit.-Punkt-Zitat (österr. KV-/Gesetzes-Konvention für
  // Unterabsätze, z.B. „lit. a", „lit. b"). Word-Boundary an beiden Enden
  // verhindert False-Positives wie „literally" oder „lit. abc".
  /\blit\.\s*[a-z]\b/i,
  // Iter 19.63.104 — Explizite KV-Regelwerk-Verweise. Wenn die LLM-
  // Begründung exakt „laut KV-Regelwerk", „gemäß KV-Anrechnungsregel"
  // oder „lt. KV-Sonderregel" formuliert, ist die Klassifikation
  // deterministisch aus dem KV abgeleitet — KEIN ANNAHME-Status. Pattern
  // sind spezifisch genug (KV gefolgt von „-Regelwerk"/„-Anrechnungsregel"/
  // „-Sonderregel"), um Fehlalarme bei generischem „laut KV" zu vermeiden.
  // Hintergrund (Iter 19.63.104, Bug-Report Kinderbetreuungsgeld):
  // Position „Bezug/Anspruch auf pauschalem Kinderbetreuungsgeld" wurde
  // fälschlich als ANNAHME getaggt, obwohl die LLM-Begründung „laut
  // KV-Regelwerk anrechenbar (max. 24 Monate)" sagte. Kein KV-Faktor-
  // Stichwort-Match auf der Tätigkeit, daher kein `kv-rule` über die
  // anderen Pfade — hier fängt das KV-Regelwerk-Phrasen-Pattern ab.
  /laut\s+kv[-\s]?(regelwerk|anrechnungsregel|anrechnungsregeln|sonderregel|sonderregeln)/i,
  /gem(?:äß|aess)\s+kv[-\s]?(regelwerk|anrechnungsregel|anrechnungsregeln|sonderregel|sonderregeln)/i,
  /lt\.\s+kv[-\s]?(regelwerk|anrechnungsregel|anrechnungsregeln|sonderregel|sonderregeln)/i,
];

export function isSvCodeBased(row) {
  if (!row) return false;
  const tae = String(row.taetigkeit || "");
  const begr = String(row.anrechenbarkeit_begruendung || "");
  const combinedText = `${tae}\n${begr}`;
  const hasSvCode = _SV_CODE_PATTERNS.some((p) => p.test(combinedText));
  if (!hasSvCode) return false;
  // Anti-False-Positive: Wenn Begründung global negiert, kein SV-Code-
  // basiertes kv-rule (Backend-Selbstwiderspruch).
  if (_hasGlobalNegation(begr)) return false;
  return true;
}

function _isNegatedContext(text, hitIdx) {
  const start = Math.max(0, hitIdx - _NEG_WINDOW);
  const context = text.slice(start, hitIdx);
  return _NEG_HINTS.some((hint) => context.includes(hint));
}

export function applyKvFactorRuleFromBegruendung(begruendung, faktorRegeln) {
  if (!begruendung || !faktorRegeln) return null;
  const t = String(begruendung).toLowerCase();
  if (!t.trim()) return null;
  // Iter 19.63.88 / 19.63.89 — Globaler Anti-False-Positive-Check: Wenn
  // die Begründung an irgendeiner Stelle eine „nicht anrechenbar"-Phrase
  // enthält, ist das semantisch eine Negativ-Aussage und darf NIE einen
  // positiven KV-Match triggern. Defense-in-Depth gegen Backend-Selbst-
  // widersprüche. Wiederverwendung von `_hasGlobalNegation` aus
  // Iter 19.63.89 (Single Source of Truth für Negation-Phrases).
  if (_hasGlobalNegation(t)) {
    return null;
  }
  // Nur affirmative Buckets — „nicht" wäre semantisch widersprüchlich
  // zu anrechenbar=true (das ist die einzige Pfade, in der dieser
  // Match überhaupt erreicht wird).
  const buckets = [
    ["haelftig", 0.5, "Hälftig"],
    ["voll", 1.0, "Voll"],
  ];
  for (const [cat, faktor, label] of buckets) {
    const keywords = faktorRegeln[cat] || [];
    for (const kw of keywords) {
      if (!kw || kw.length < 3) continue;
      // Iter 19.74 (Baustein D) — Solo-Keyword-Guard: Single-Word-Tokens
      // < 7 Zeichen sind zu generisch, um als deterministischer Begründungs-
      // basierter KV-Regel-Trigger zu dienen (Falsch-Matches auf Wörter
      // wie „Bezug" in „AMS-Bezug", „Haft" in „Haftpflicht"). Diese Regel
      // spiegelt den Backend-Parser-Guard (Iter 19.74) und schützt Frontend
      // vor stale/gecachten KV-Regel-Objekten aus Pre-Iter-19.74-Backends.
      if (kw.length < 7 && !/[\s-]/.test(kw)) continue;
      let hitKw = null;
      let hitIdx = -1;
      // Iter 19.70 (M12) — kwIndexOf statt includes/indexOf (Compound-Guard).
      let idx = kwIndexOf(kw, t);
      if (idx >= 0) {
        hitKw = kw;
        hitIdx = idx;
      } else if (kw.length >= 5) {
        idx = kwIndexOf(kw.slice(0, -1), t);
        if (idx >= 0) {
          hitKw = kw;
          hitIdx = idx;
        }
      }
      if (hitKw && hitIdx >= 0 && !_isNegatedContext(t, hitIdx)) {
        return {
          faktor,
          anrechenbar: faktor > 0,
          begruendung: `[Faktor ${faktor} gesetzt gemäß KV-Regel „${label} anrechenbar: ${hitKw}" — Stichwort aus Begründungstext.]`,
          matched: hitKw,
          category: cat,
          source: "begruendung",
        };
      }
    }
  }
  return null;
}



/**
 * Merge fresh AI-auto-matched Ausbildungs-Items into an existing list
 * without nuking user edits. Used by both handleStep2Submit and
 * handleReanalyze.
 *
 * Logic:
 *   - Keep EVERY existing row (user-edited or not) — never delete.
 *   - For each incoming row: if its `label` already exists, skip it
 *     (the user might have edited the existing one — we don't overwrite).
 *   - Otherwise: append it (marks added rows as `ai_matched: true`).
 *
 * Iter 19.32 fix for the re-analyze gap: Lehrabschlussprüfung detected
 * from a Q&A answer used to be dropped because the existing Kassakraft-row
 * triggered the old length===0 guard.
 */
/**
 * Merge freshly-received auto-matched ausbildungs items into an existing
 * list without clobbering user edits.
 *
 * Rules:
 *   - Iter 20.36.f14.16: If an incoming item has `_source="step3_file"`
 *     and the matching existing row has `_needs_proof=true` (i.e. still a
 *     Step-2-Q&A vorschlag ohne Nachweis), REPLACE the existing row.
 *     The Step-3 document IS the proof → auto-confirm. Preserve the
 *     user's anrechenbar-toggle state if they've already interacted with
 *     it (fällt aber standardmäßig zurück auf incoming.anrechenbar).
 *   - Iter 19.32: For each incoming row: if its `label` already exists
 *     as a user-confirmed / manually added / already-file-backed entry,
 *     skip it (the user might have edited it — we don't overwrite).
 *   - Otherwise: append it (marks added rows as `ai_matched: true`).
 *
 * Iter 19.32 fix for the re-analyze gap: Lehrabschlussprüfung detected
 * from a Q&A answer used to be dropped because the existing Kassakraft-row
 * triggered the old length===0 guard.
 */
export function mergeAusbildungsAutoMatches(existing, incoming) {
  const existingArr = Array.isArray(existing) ? existing : [];
  const incomingArr = Array.isArray(incoming) ? incoming : [];
  if (existingArr.length === 0) return incomingArr;

  // Build lookup by lowercased label for the existing list.
  const norm = (s) => (typeof s === "string" ? s.toLowerCase().trim() : "");
  const existingByLabel = new Map();
  existingArr.forEach((r, idx) => {
    const lbl = norm(r?.label);
    if (lbl) existingByLabel.set(lbl, { row: r, idx });
  });

  // First pass: apply Step-3-file upgrades to Step-2-Q&A-Vorschläge.
  const result = [...existingArr];
  const upgradedLabels = new Set();
  for (const inc of incomingArr) {
    const lbl = norm(inc?.label);
    if (!lbl) continue;
    const hit = existingByLabel.get(lbl);
    if (!hit) continue; // Handled in append-pass below.
    const isStep3FileMatch = inc?._source === "step3_file";
    const existingNeedsProof = hit.row?._needs_proof === true;
    if (isStep3FileMatch && existingNeedsProof) {
      // Upgrade: replace Step-2 row with Step-3-file row (auto-confirm).
      // Preserve user's anrechenbar-toggle if they've explicitly disabled it.
      const userDisabled = hit.row.anrechenbar === false;
      result[hit.idx] = {
        ...inc,
        anrechenbar: userDisabled ? false : (inc.anrechenbar !== false),
        _needs_proof: false,
      };
      upgradedLabels.add(lbl);
    }
  }

  // Second pass: append new labels that weren't in the existing list.
  const finalLabels = new Set(result.map((r) => norm(r?.label)).filter(Boolean));
  for (const inc of incomingArr) {
    const lbl = norm(inc?.label);
    if (!lbl || finalLabels.has(lbl) || upgradedLabels.has(lbl)) continue;
    result.push(inc);
    finalLabels.add(lbl);
  }
  return result;
}


/**
 * Iter 19.63.38 — Math-Konsistenz: Single Source of Truth für die
 * Effective-Total-Berechnung in Step 3 (Sidebar + Modal-Final).
 *
 * Math (identisch zur Modal-Kaskade in BerechnungsdetailsModal.jsx und
 * zur Backend-Logik in `final_recommendation.py` Z. 778-782 sowie zum
 * Python-Mirror `vordienstzeiten.compute_effective_total_days`):
 *
 *   preCap          = backendVordienstDays + Σ normalBonusDays
 *   cappedPreBypass = cap > 0 ? min(preCap, cap) : preCap
 *   final           = cappedPreBypass + Σ bypassBonusDays
 *
 * - NORMALE Bonuszeiten (`bypass_cap=false`, z.B. HAK-Matura): mit-gecappt.
 * - BYPASS-Bonuszeiten (`bypass_cap=true`, z.B. KV-Sonderregel Kassakräfte):
 *   nach Cap addiert (= zählen ÜBER die Höchstgrenze hinaus).
 *
 * **WICHTIG:** Bei Math-Änderungen MUSS der Python-Mirror in
 * `/app/backend/vordienstzeiten.py compute_effective_total_days()`
 * synchron angepasst werden. Siehe `/app/MATH_CONSISTENCY.md`.
 *
 * @param {object} args
 * @param {number} args.vordienstDays - backendAggregate.totalDays (= Vordienst-Total, bereits gecappt vom Backend)
 * @param {Array}  args.ausbildungszeiten - [{anrechenbar, bypass_cap, months}, ...]
 * @param {number} args.capMonths - KV-Höchstgrenze in Monaten (0 = kein Cap)
 * @returns {object} {effectiveTotalDays, jahre, monate, tage, display, exceeded,
 *                    normalBonusMonths, bypassMonths, preCapDays, cappedPreBypassDays}
 */
export function computeEffectiveTotal({ vordienstDays = 0, ausbildungszeiten = [], capMonths = 0 }) {
  const active = (Array.isArray(ausbildungszeiten) ? ausbildungszeiten : []).filter(
    (a) => a && a.anrechenbar && (Number(a.months) || 0) > 0,
  );
  const normalBonusMonths = active
    .filter((a) => !a.bypass_cap)
    .reduce((sum, a) => sum + Number(a.months), 0);
  const bypassMonths = active
    .filter((a) => a.bypass_cap)
    .reduce((sum, a) => sum + Number(a.months), 0);

  const normalBonusDays = normalBonusMonths * 30;
  const bypassDays = bypassMonths * 30;
  const capDays = capMonths > 0 ? capMonths * 30 : 0;
  const v = Number(vordienstDays) || 0;

  const preCapDays = v + normalBonusDays;
  const cappedPreBypassDays = capDays > 0 ? Math.min(preCapDays, capDays) : preCapDays;
  const effectiveTotalDays = cappedPreBypassDays + bypassDays;

  const rounded = Math.floor(effectiveTotalDays + 0.5);
  const jahre = Math.floor(rounded / 360);
  const monate = Math.floor((rounded % 360) / 30);
  const tage = rounded % 30;
  const parts = [];
  if (jahre) parts.push(`${jahre} J.`);
  if (monate) parts.push(`${monate} M.`);
  if (tage || (!jahre && !monate)) parts.push(`${tage} T.`);

  return {
    effectiveTotalDays,
    jahre,
    monate,
    tage,
    display: parts.join(" "),
    exceeded: capDays > 0 && preCapDays > capDays,
    normalBonusMonths,
    bypassMonths,
    preCapDays,
    cappedPreBypassDays,
  };
}
